import {
  useCallback, useEffect, useMemo, useRef, useState,
} from 'react';
import { useTokens } from '../theme.jsx';
import { notifyActionError, notifyRequestError } from '../actionErrors.js';
import { Spinner } from '../Spinner.jsx';
import { getInstitutionAiAttachmentUrl, uploadInstitutionAiAttachment } from '../services/institutions.js';
import { AnimatedCollapse, AnimatedPopover } from './AnimatedReveal.jsx';
import AssistantMarkdown from './AssistantMarkdown.jsx';
import useAiAttachments, { AI_ATTACHMENT_ACCEPT, MAX_AI_ATTACHMENTS } from './useAiAttachments.js';
import { isAiPausedError, aiPausedMessage, aiPausedTurnMessage } from './aiPaused.js';

const HISTORY_LIMIT = 20;
// Short settle window for the common sync-path case (finalize already landed).
const SETTLE_RETRY_DELAYS_MS = [0, 100, 250, 500, 750];
// Durable/worker turns can emit `done { reload: true }` while Mongo is still
// `status:'streaming'`. Poll like the main-app client until the node settles.
const RELOAD_SETTLE_ATTEMPTS = 8;
const RELOAD_SETTLE_INTERVAL_MS = 1000;
// Resume / history rows live outside the open stream, so poll while anything is
// still generating and stop as soon as the server reports terminal turns.
const RUNNING_HISTORY_POLL_MS = 4000;

function conversationIsSettled(conversation, assistantMessageId = null) {
  if (!conversation) { return false; }
  if ((conversation.pendingTurns || []).length) { return false; }
  const messages = conversation.messages || [];
  // Any streaming placeholder means the turn is still live — do NOT treat a
  // missing assistantMessageId match as settled (local UUID vs server ObjectId
  // used to false-settle and adopt a broken activeLeafId → empty thread).
  if (messages.some((message) => { return message.status === 'streaming'; })) {
    return false;
  }
  if (assistantMessageId) {
    const node = messages.find((message) => {
      return String(message._id) === String(assistantMessageId);
    });
    if (!node) { return false; }
  }
  return true;
}

/** Prefer server content; fall back to the in-memory streamed text when Mongo is still empty. */
function mergeStreamedContentIntoConversation(conversation, localNodes = []) {
  if (!conversation) { return conversation; }
  const localById = new Map(
    (localNodes || []).map((node) => { return [String(node.id), node]; }),
  );
  // Also index local assistant nodes in order for id-mismatch fallback.
  const localAssistants = (localNodes || []).filter((node) => { return node.role === 'assistant'; });
  let assistantIdx = 0;
  const messages = (conversation.messages || []).map((message) => {
    const id = String(message._id || message.id || '');
    const local = localById.get(id)
      || (message.role === 'assistant' ? localAssistants[assistantIdx] : null);
    if (message.role === 'assistant') { assistantIdx += 1; }
    const localContent = local?.content || '';
    const serverContent = message.content || '';
    if (serverContent || !localContent) { return message; }
    return { ...message, content: localContent };
  });
  return { ...conversation, messages };
}

function forceTerminalConversation(conversation, localNodes = []) {
  const merged = mergeStreamedContentIntoConversation(conversation, localNodes);
  return {
    ...merged,
    pendingTurns: [],
    messages: (merged.messages || []).map((message) => {
      if (message.status !== 'streaming') { return message; }
      return { ...message, status: 'done' };
    }),
  };
}

export const INSTITUTION_QUICK_ACTIONS = [
  {
    key: 'health',
    label: 'Program health',
    icon: 'sparkles',
    items: [
      ['Summarize our program health', 'Summarize our program health and the most important trend.'],
      ['Show adoption and engagement', 'Explain our current adoption, engagement, and coffee-chat trends.'],
      ['Flag what needs attention', 'What needs our attention most right now, and why?'],
    ],
  },
  {
    key: 'billing',
    label: 'Billing',
    icon: 'card',
    items: [
      ['Explain our current plan', 'Explain our plan, current usage, seats, overage, and recent invoices.'],
      ['Summarize recent invoices', 'Summarize our recent invoices and flag anything outstanding.'],
      ['Check seat capacity', 'How much sponsored-seat capacity do we have left?'],
    ],
  },
  {
    key: 'privacy',
    label: 'Privacy',
    icon: 'shield',
    items: [
      ['What institution data can we see?', 'What institution data can we see under the Privacy Policy and Terms?'],
      ['Explain suppressed metrics', 'Explain why some institution metrics may be privacy-suppressed.'],
      ['Link the governing policies', 'Show me the official privacy and terms sources that govern this portal.'],
    ],
  },
  {
    key: 'next',
    label: 'What’s next',
    icon: 'compass',
    items: [
      ['Recommend our next action', 'Based on our authorized program data, what should we do next?'],
      ['Draft a support request', 'Draft an email to the Coffee Chats team about an issue with our program.'],
      ['Prepare an operating brief', 'Prepare a concise operating brief for our institution administrator team.'],
    ],
  },
];

export const INSTITUTION_PROJECT_CARDS = [
  {
    icon: 'chart',
    title: 'Program review',
    description: 'Turn current adoption, engagement, and outcome signals into a clear brief.',
    prompt: 'Give me a program review with the most important metrics, trends, risks, and next actions.',
  },
  {
    icon: 'users',
    title: 'Seats & funding',
    description: 'Understand sponsored seats, usage, plan limits, overage, and invoices.',
    prompt: 'Review our seats, funding, plan usage, overage, and billing status.',
  },
  {
    icon: 'mail',
    title: 'Contact Coffee Chats',
    description: 'Draft a message to our team. You review it before anything is sent.',
    prompt: 'Draft an email to the Coffee Chats team about an issue with our program.',
  },
];

const RESPONSE_MODES = [
  ['fast', 'Fast', 'bolt'],
  ['balanced', 'Balanced', 'balanced'],
  ['deep', 'Deep', 'sparkles'],
];

export const SUPER_ADMIN_QUICK_ACTIONS = [
  {
    key: 'growth',
    label: 'Growth',
    icon: 'sparkles',
    items: [
      ['Summarize platform health', 'Summarize platform health, growth, and the most important trend.'],
      ['Compare B2C vs institutions', 'Compare B2C and institution segment engagement and revenue.'],
      ['Flag what needs attention', 'What needs our attention most right now across the product?'],
    ],
  },
  {
    key: 'revenue',
    label: 'Revenue',
    icon: 'card',
    items: [
      ['Explain current MRR', 'Explain current MRR, paid users, seat revenue, and gross margin.'],
      ['Review burn and runway', 'Review burn, runway, fixed costs, and AI COGS.'],
      ['Assess churn risk', 'What do recent churn and retention signals suggest?'],
    ],
  },
  {
    key: 'support',
    label: 'Support',
    icon: 'shield',
    items: [
      ['Summarize support backlog', 'Summarize the support inbox backlog and recent tickets.'],
      ['Prioritize urgent tickets', 'Which open support tickets look most urgent and why?'],
      ['Draft a support reply', 'Draft a concise reply template for a common support issue.'],
    ],
  },
  {
    key: 'institutions',
    label: 'Institutions',
    icon: 'compass',
    items: [
      ['Review institution roster', 'Review institution roster health, seats, and pending approvals.'],
      ['Recommend next operator action', 'Based on authorized platform data, what should we do next?'],
      ['Prepare a founder brief', 'Prepare a concise operating brief for the Coffee Chats team.'],
    ],
  },
];

export const SUPER_ADMIN_PROJECT_CARDS = [
  {
    icon: 'chart',
    title: 'Platform review',
    description: 'Turn growth, revenue, retention, and COGS into a clear founder brief.',
    prompt: 'Give me a platform review with the most important metrics, trends, risks, and next actions.',
  },
  {
    icon: 'users',
    title: 'Institutions & seats',
    description: 'Understand institution roster health, seat utilization, and pending approvals.',
    prompt: 'Review institution accounts, seat utilization, pending admin requests, and funding requests.',
  },
  {
    icon: 'mail',
    title: 'Support triage',
    description: 'Summarize the support backlog and recommend what to handle first.',
    prompt: 'Summarize the support inbox backlog and recommend triage priorities.',
  },
];

export const REQUEST_TYPES = [
  ['GENERAL', 'General'],
  ['BILLING', 'Billing'],
  ['FEATURE', 'Feature request'],
  ['FEEDBACK', 'Feedback'],
];

function Icon({ name, size = 18 }) {
  const common = {
    'aria-hidden': true,
    width: size,
    height: size,
    viewBox: '0 0 24 24',
    fill: 'none',
    stroke: 'currentColor',
    strokeWidth: 1.8,
    strokeLinecap: 'round',
    strokeLinejoin: 'round',
  };
  const paths = {
    edit: <><path d="M12 20h9" /><path d="M16.5 3.5a2.1 2.1 0 0 1 3 3L8 18l-4 1 1-4Z" /></>,
    clock: <><circle cx="12" cy="12" r="9" /><path d="M12 7v5l3 2" /></>,
    plus: <path d="M12 5v14M5 12h14" />,
    arrow: <path d="m6 12 6-6 6 6M12 6v12" />,
    chevron: <path d="m8 10 4 4 4-4" />,
    sparkles: <><path d="m12 3 1.2 3.8L17 8l-3.8 1.2L12 13l-1.2-3.8L7 8l3.8-1.2Z" /><path d="m18 14 .8 2.2L21 17l-2.2.8L18 20l-.8-2.2L15 17l2.2-.8Z" /></>,
    card: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="M3 10h18" /></>,
    shield: <path d="M12 3 5 6v5c0 4.6 2.8 8.2 7 10 4.2-1.8 7-5.4 7-10V6Z" />,
    compass: <><circle cx="12" cy="12" r="9" /><path d="m15 9-2 4-4 2 2-4Z" /></>,
    chart: <><path d="M4 20V10M10 20V4M16 20v-7M22 20H2" /></>,
    users: <><circle cx="9" cy="8" r="3" /><path d="M3.5 19c.5-3.5 2.3-5 5.5-5s5 1.5 5.5 5M16 8h5M18.5 5.5v5" /></>,
    mail: <><rect x="3" y="5" width="18" height="14" rx="2" /><path d="m3 7 9 6 9-6" /></>,
    bolt: <path d="m13 2-8 12h7l-1 8 8-12h-7Z" />,
    balanced: <><path d="M12 4a8 8 0 1 0 0 16Z" /><path d="M12 4a8 8 0 0 1 0 16Z" opacity=".35" /></>,
    check: <path d="m5 12 4 4L19 6" />,
    copy: <><rect x="9" y="9" width="11" height="11" rx="2" /><path d="M15 9V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7a2 2 0 0 0 2 2h3" /></>,
    thumbsUp: <><path d="M7 10v10H4V10Z" /><path d="M7 18c3 2 7 2 10 1l2-7a2 2 0 0 0-2-2h-4l1-4c0-2-2-3-3-1l-4 6" /></>,
    thumbsDown: <><path d="M7 14V4H4v10Z" /><path d="M7 6c3-2 7-2 10-1l2 7a2 2 0 0 1-2 2h-4l1 4c0 2-2 3-3 1l-4-6" /></>,
    refresh: <><path d="M20 6v5h-5" /><path d="M19 11a8 8 0 1 0 1 5" /></>,
    left: <path d="m15 18-6-6 6-6" />,
    right: <path d="m9 18 6-6-6-6" />,
    // Resume-banner arrows (match main-client Feather corner-up-left / arrow-right).
    cornerUpLeft: <><path d="M9 14 4 9l5-5" /><path d="M20 20v-7a4 4 0 0 0-4-4H4" /></>,
    arrowRight: <><path d="M5 12h14" /><path d="m12 5 7 7-7 7" /></>,
    square: <rect x="5" y="5" width="14" height="14" rx="2" fill="currentColor" stroke="none" />,
    x: <path d="m7 7 10 10M17 7 7 17" />,
    alert: <><path d="m10.3 4.3-7.6 13.2A2 2 0 0 0 4.4 20.5h15.2a2 2 0 0 0 1.7-3L13.7 4.3a2 2 0 0 0-3.4 0Z" /><path d="M12 9v4M12 17h.01" /></>,
    file: <><path d="M6 3h8l4 4v14H6Z" /><path d="M14 3v5h5M9 13h6M9 17h6" /></>,
    image: <><rect x="3" y="4" width="18" height="16" rx="2" /><circle cx="9" cy="10" r="2" /><path d="m4 17 5-4 3 3 2-2 6 4" /></>,
    download: <><path d="M12 3v12M7 10l5 5 5-5" /><path d="M5 21h14" /></>,
  };
  return <svg {...common}>{paths[name] || paths.sparkles}</svg>;
}

function useDismiss(open, setOpen, ref) {
  useEffect(() => {
    if (!open) { return undefined; }
    const outside = (event) => { if (!ref.current?.contains(event.target)) { setOpen(false); } };
    const escape = (event) => { if (event.key === 'Escape') { setOpen(false); } };
    document.addEventListener('pointerdown', outside);
    document.addEventListener('keydown', escape);
    return () => {
      document.removeEventListener('pointerdown', outside);
      document.removeEventListener('keydown', escape);
    };
  }, [open, ref, setOpen]);
}

function AttachmentProgress({ value = 0 }) {
  const progress = Math.max(0, Math.min(1, Number(value) || 0));
  const radius = 9;
  const circumference = 2 * Math.PI * radius;
  return (
    <svg aria-label={`Uploading ${Math.round(progress * 100)}%`} role="img" width="24" height="24" viewBox="0 0 24 24" className="-rotate-90">
      <circle cx="12" cy="12" r={radius} fill="none" stroke="currentColor" strokeWidth="2" opacity="0.2" />
      <circle cx="12" cy="12" r={radius} fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeDasharray={circumference} strokeDashoffset={circumference * (1 - progress)} />
    </svg>
  );
}

function attachmentPreviewUrl(attachment, getAttachmentUrl) {
  return attachment?.previewUrl || getAttachmentUrl?.(attachment?.fileId);
}

function AttachmentChip({ attachment, onRemove, onPreview, getAttachmentUrl }) {
  const t = useTokens();
  if (!attachment) { return null; }
  const isImage = attachment.isImage || attachment.kind === 'image';
  const name = attachment.name || attachment.originalFilename || (isImage ? 'Image' : 'File');
  const previewUrl = attachmentPreviewUrl(attachment, getAttachmentUrl);
  const interactive = Boolean(onPreview && previewUrl && !attachment.uploading && !attachment.error);

  if (isImage && previewUrl && !attachment.error) {
    return (
      <div className="relative h-[72px] w-[72px] shrink-0 overflow-hidden rounded-xl border bg-white" style={{ borderColor: t.border }}>
        <button type="button" disabled={!interactive} onClick={() => { onPreview?.(attachment); }} aria-label={`Preview attachment ${name}`} className="h-full w-full disabled:cursor-default">
          <img src={previewUrl} alt={name} className="h-full w-full object-cover" />
        </button>
        {attachment.uploading ? <span className="absolute inset-0 flex items-center justify-center bg-white/70" style={{ color: t.heading }}><AttachmentProgress value={attachment.progress} /></span> : null}
        {onRemove ? <button type="button" onClick={() => { onRemove(attachment.localId); }} aria-label={`Remove attachment ${name}`} className={`absolute right-1 top-1 flex h-5 w-5 items-center justify-center rounded-full border bg-white shadow-sm focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.muted, borderColor: t.border }}><Icon name="x" size={11} /></button> : null}
      </div>
    );
  }

  const body = (
    <>
      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-lg" style={{ backgroundColor: t.statBg, color: attachment.error ? t.danger : t.heading }}>
        {attachment.uploading ? <AttachmentProgress value={attachment.progress} /> : <Icon name={isImage ? 'image' : 'file'} size={16} />}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block max-w-[180px] truncate text-xs font-semibold" style={{ color: attachment.error ? t.danger : t.heading }}>{name}</span>
        {attachment.error ? <span className="block max-w-[180px] truncate text-[11px]" style={{ color: t.danger }}>{attachment.error}</span> : attachment.truncated ? <span className="block text-[11px]" style={{ color: t.muted }}>Text truncated</span> : null}
      </span>
    </>
  );
  return (
    <div className="flex min-h-[48px] max-w-[260px] items-center gap-2 rounded-xl border bg-white p-2" style={{ borderColor: attachment.error ? '#EAA38F' : t.border }}>
      {interactive ? <button type="button" onClick={() => { onPreview(attachment); }} aria-label={`Preview attachment ${name}`} className={`flex min-w-0 flex-1 items-center gap-2 rounded-lg text-left focus:outline-none focus-visible:ring-2 ${t.focusRing}`}>{body}</button> : <div className="flex min-w-0 flex-1 items-center gap-2">{body}</div>}
      {onRemove ? <button type="button" onClick={() => { onRemove(attachment.localId); }} aria-label={`Remove attachment ${name}`} className={`shrink-0 rounded-md p-1 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.muted }}><Icon name="x" size={13} /></button> : null}
    </div>
  );
}

function AttachmentPreviewModal({ attachment, onClose, getAttachmentUrl }) {
  const t = useTokens();
  useEffect(() => {
    if (!attachment) { return undefined; }
    const escape = (event) => { if (event.key === 'Escape') { onClose(); } };
    document.addEventListener('keydown', escape);
    return () => { document.removeEventListener('keydown', escape); };
  }, [attachment, onClose]);
  if (!attachment) { return null; }
  const name = attachment.originalFilename || attachment.name || 'Attachment';
  const mimeType = attachment.mimeType || '';
  const isImage = attachment.isImage || attachment.kind === 'image' || mimeType.startsWith('image/');
  const isPdf = mimeType === 'application/pdf' || /\.pdf$/i.test(name);
  const url = attachmentPreviewUrl(attachment, getAttachmentUrl);
  const downloadUrl = attachment.fileId ? getAttachmentUrl?.(attachment.fileId, { download: true }) : url;
  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-[#2B1B12]/45 p-4" role="presentation" onMouseDown={(event) => { if (event.target === event.currentTarget) { onClose(); } }}>
      <section role="dialog" aria-modal="true" aria-label={`Preview ${name}`} className="flex max-h-[88dvh] w-full max-w-4xl flex-col overflow-hidden rounded-2xl border bg-white shadow-2xl" style={{ borderColor: t.border }}>
        <header className="flex items-center justify-between gap-3 border-b px-4 py-3" style={{ borderColor: t.hairline }}>
          <p className="min-w-0 truncate text-sm font-semibold" style={{ color: t.heading }}>{name}</p>
          <div className="flex items-center gap-1">
            {downloadUrl ? <a href={downloadUrl} aria-label={`Download ${name}`} className={`rounded-lg p-2 hover:bg-[#FFF9EE] focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.body }}><Icon name="download" size={17} /></a> : null}
            <button type="button" onClick={onClose} aria-label="Close attachment preview" className={`rounded-lg p-2 hover:bg-[#FFF9EE] focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.body }}><Icon name="x" size={17} /></button>
          </div>
        </header>
        <div className="min-h-[280px] flex-1 overflow-auto bg-[#FBF8F3] p-4">
          {isImage && url ? <img src={url} alt={name} className="mx-auto max-h-[72dvh] max-w-full rounded-lg object-contain" /> : null}
          {isPdf && url ? <iframe src={url} title={name} className="h-[70dvh] w-full rounded-lg border bg-white" style={{ borderColor: t.border }} /> : null}
          {!isImage && !isPdf ? (
            <div className="flex min-h-[280px] flex-col items-center justify-center text-center">
              <span className="flex h-12 w-12 items-center justify-center rounded-xl" style={{ backgroundColor: t.statBg, color: t.heading }}><Icon name="file" size={23} /></span>
              <p className="mt-3 text-sm font-semibold" style={{ color: t.heading }}>{name}</p>
              <p className="mt-1 text-sm" style={{ color: t.muted }}>Download this file to open it.</p>
              {downloadUrl ? <a href={downloadUrl} className={`mt-4 inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm font-semibold focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ backgroundColor: t.primaryBg, color: t.primaryText }}><Icon name="download" size={15} />Download</a> : null}
            </div>
          ) : null}
        </div>
      </section>
    </div>
  );
}

function attachmentBreadcrumb(item) {
  if (!item?.attachments?.length) { return ''; }
  const names = item.attachments
    .map((attachment) => { return String(attachment.originalFilename || attachment.name || 'file').replace(/[\[\]\r\n]/g, ' ').slice(0, 160); })
    .join(', ');
  return names ? `\n[attached: ${names}]` : '';
}

function readHistory(key) {
  try {
    const value = JSON.parse(window.localStorage.getItem(key) || '[]');
    return Array.isArray(value) ? value.slice(0, HISTORY_LIMIT) : [];
  } catch {
    return [];
  }
}

function writeHistory(key, conversations) {
  try { window.localStorage.setItem(key, JSON.stringify(conversations.slice(0, HISTORY_LIMIT))); } catch { /* optional */ }
}

function isDurableConversationId(id) {
  return /^[a-fA-F0-9]{24}$/.test(String(id || ''));
}

function conversationNodes(conversation) {
  const nodes = conversation?.nodes || conversation?.messages;
  return Array.isArray(nodes) ? nodes : [];
}

function conversationLooksInFlight(conversation) {
  if (conversation?.running) { return true; }
  return conversationNodes(conversation).some((node) => {
    return Boolean(node?.pending || node?.status === 'streaming');
  });
}

function serverConversationNodes(conversation) {
  return (conversation?.messages || []).map((item) => {
    const meta = (item.modules || []).find((module) => { return module?.type === 'institution-ai-meta'; });
    const atMs = item.at ? new Date(item.at).getTime() : NaN;
    // Only seed startedAt for in-flight streaming nodes (reattach/reload). Settled
    // replies must not inherit message `at` or every assistant bubble grows a
    // "Thought for Xs" rail — mirrors main client's thinkingStartedAt fallback.
    const streaming = item.status === 'streaming';
    return {
      ...item,
      id: String(item._id || item.id),
      parentId: item.parentId ? String(item.parentId) : null,
      pending: streaming,
      startedAt: streaming && Number.isFinite(atMs) ? atMs : item.startedAt,
      sources: item.sources || meta?.sources || [],
      founderAlertDraft: item.founderAlertDraft || meta?.founderAlertDraft || null,
      // A turn the AI kill switch refused renders the paused notice.
      aiPaused: item.role === 'assistant' ? (aiPausedTurnMessage(item) || undefined) : undefined,
    };
  });
}

export function formatChatTime(value, nowValue = Date.now()) {
  const ts = value ? new Date(value).getTime() : 0;
  const now = new Date(nowValue);
  if (!Number.isFinite(ts) || ts <= 0 || !Number.isFinite(now.getTime())) { return ''; }
  const date = new Date(ts);
  const diffMinutes = Math.max(0, Math.round((now.getTime() - ts) / 60000));
  if (diffMinutes < 1) { return 'Just now'; }
  if (diffMinutes < 60) { return `${diffMinutes}m ago`; }
  if (date.toDateString() === now.toDateString()) { return `${Math.round(diffMinutes / 60)}h ago`; }
  const yesterday = new Date(now);
  yesterday.setDate(now.getDate() - 1);
  if (date.toDateString() === yesterday.toDateString()) { return 'Yesterday'; }
  const startOfDay = (day) => { return new Date(day.getFullYear(), day.getMonth(), day.getDate()); };
  if (Math.round((startOfDay(now) - startOfDay(date)) / 86400000) < 7) {
    return date.toLocaleDateString(undefined, { weekday: 'short' });
  }
  if (date.getFullYear() === now.getFullYear()) {
    return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
  }
  return date.toLocaleDateString(undefined, { month: 'short', day: 'numeric', year: 'numeric' });
}

function normalizeNodes(value = []) {
  let previousId = null;
  return value.map((item, index) => {
    const id = item.id || `legacy-${index}-${Date.now()}`;
    const node = { ...item, id, parentId: Object.prototype.hasOwnProperty.call(item, 'parentId') ? item.parentId : previousId };
    previousId = id;
    return node;
  });
}

function pathToLeaf(nodes, leafId) {
  if (!leafId) { return []; }
  const byId = new Map(nodes.map((node) => { return [node.id, node]; }));
  const path = [];
  let current = byId.get(leafId);
  const seen = new Set();
  while (current && !seen.has(current.id)) {
    seen.add(current.id);
    path.unshift(current);
    current = current.parentId ? byId.get(current.parentId) : null;
  }
  return path;
}

function latestLeafUnder(nodes, rootId) {
  let current = nodes.find((node) => { return node.id === rootId; });
  if (!current) { return rootId; }
  for (;;) {
    const children = nodes
      .filter((node) => { return node.parentId === current.id; })
      .sort((a, b) => { return String(a.at || '').localeCompare(String(b.at || '')); });
    if (!children.length) { return current.id; }
    current = children[children.length - 1];
  }
}

function branchSiblings(nodes, item) {
  return nodes.filter((node) => { return node.parentId === item.parentId && node.role === item.role; });
}

function SourceLinks({ sources }) {
  const t = useTokens();
  if (!sources?.length) { return null; }
  return (
    <div className="mt-4 border-t pt-3" style={{ borderColor: t.hairline }}>
      <p className="text-[11px] uppercase tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Sources</p>
      <div className="mt-2 flex flex-wrap gap-2">
        {sources.map((source) => {
          const external = /^https?:\/\//.test(source.url || '');
          return (
            <a
              key={source.id}
              href={source.url}
              target={external ? '_blank' : undefined}
              rel={external ? 'noreferrer' : undefined}
              className={`rounded-full border px-3 py-1.5 text-xs font-medium hover:opacity-75 focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
              style={{ color: t.body, borderColor: t.border, backgroundColor: t.surface }}
            >
              {source.title}
            </a>
          );
        })}
      </div>
    </div>
  );
}

export function FounderAlertDraft({ draft, submitSupportRequest }) {
  const t = useTokens();
  const [type, setType] = useState(draft.type || 'GENERAL');
  const [typeOpen, setTypeOpen] = useState(false);
  const [subject, setSubject] = useState(draft.subject || '');
  const [message, setMessage] = useState(draft.message || '');
  const [confirming, setConfirming] = useState(false);
  const [sending, setSending] = useState(false);
  const [sent, setSent] = useState(null);
  const typeRef = useRef(null);
  useDismiss(typeOpen, setTypeOpen, typeRef);

  const send = async () => {
    if (!submitSupportRequest) { return; }
    setSending(true);
    try {
      const result = await submitSupportRequest({ type, subject, message });
      setSent(result);
      setConfirming(false);
    } catch (requestError) {
      // A reload shows this card as Not sent again, so point at the receipt.
      notifyActionError(
        requestError,
        'Could not send your request to Coffee Chats. Nothing was marked sent.',
        'Could not confirm the request went through. Check your email for the receipt before sending again.',
      );
    } finally {
      setSending(false);
    }
  };

  if (sent) {
    return (
      <div className="mt-4 rounded-xl border p-4" style={{ borderColor: '#8DA788', backgroundColor: '#F4F8F2' }} role="status">
        <p className="font-semibold" style={{ color: '#31572C' }}>Request sent</p>
        <p className="mt-1 text-sm" style={{ color: t.body }}>
          {sent.notification?.sent > 0
            ? `${sent.notification.sent} platform administrator${sent.notification.sent === 1 ? '' : 's'} notified. `
            : 'The request is in the support inbox. '}
          Ticket {sent.request?.ticketId || 'created'}; a receipt was sent to your account email.
        </p>
      </div>
    );
  }

  const inputStyle = { borderColor: t.inputBorder, backgroundColor: t.inputBg, color: t.inputText };
  const valid = subject.trim().length >= 4 && message.trim().length >= 20;
  return (
    <div className="mt-4 rounded-2xl border p-5" style={{ borderColor: t.border, backgroundColor: t.statBg }}>
      <div className="flex items-start justify-between gap-4">
        <div>
          <p className="text-[11px] uppercase tracking-[0.1em]" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Support request draft</p>
          <h3 className="mt-1 text-lg" style={{ color: t.heading, fontFamily: t.fonts.display }}>Review before anything is sent</h3>
        </div>
        <span className="rounded-full border px-2.5 py-1 text-[11px]" style={{ borderColor: t.border, color: t.muted }}>Not sent</span>
      </div>
      <div ref={typeRef} className="relative mt-4 block text-sm font-medium" style={{ color: t.body }}>
        <span id="founder-request-type-label">Request type</span>
        <button
          type="button"
          aria-labelledby="founder-request-type-label founder-request-type-value"
          aria-haspopup="listbox"
          aria-expanded={typeOpen}
          onClick={() => { setTypeOpen((open) => { return !open; }); }}
          className={`mt-1.5 flex w-full items-center justify-between rounded-lg border px-3 py-2.5 text-left focus:outline-none focus-visible:ring-2 ${t.focusRing}`}
          style={inputStyle}
        >
          <span id="founder-request-type-value">{REQUEST_TYPES.find(([value]) => { return value === type; })?.[1] || 'General'}</span>
          <Icon name="chevron" size={15} />
        </button>
        {typeOpen ? (
          <div role="listbox" aria-labelledby="founder-request-type-label" className="absolute left-0 right-0 top-full z-50 mt-1.5 rounded-xl border p-1.5 shadow-xl" style={{ backgroundColor: t.surface, borderColor: t.border }}>
            {REQUEST_TYPES.map(([value, label]) => {
              return (
                <button key={value} type="button" role="option" aria-selected={type === value} onClick={() => { setType(value); setTypeOpen(false); }} className="flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-left text-sm hover:bg-[#FFF7EA]" style={{ color: t.body }}>
                  <span>{label}</span>{type === value ? <Icon name="check" size={15} /> : null}
                </button>
              );
            })}
          </div>
        ) : null}
      </div>
      <label className="mt-3 block text-sm font-medium" style={{ color: t.body }}>
        Subject
        <input value={subject} maxLength={120} onChange={(event) => { setSubject(event.target.value); }} className={`mt-1.5 block w-full rounded-md border px-3 py-2.5 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={inputStyle} />
      </label>
      <label className="mt-3 block text-sm font-medium" style={{ color: t.body }}>
        Message
        <textarea value={message} maxLength={5000} onChange={(event) => { setMessage(event.target.value); }} className={`mt-1.5 block min-h-[150px] w-full resize-y rounded-md border px-3 py-3 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={inputStyle} />
      </label>
      {confirming ? (
        <div className="mt-4 rounded-xl border p-4" style={{ borderColor: '#D9A441', backgroundColor: '#FFF8E8' }}>
          <p className="font-semibold" style={{ color: t.heading }}>Send this to the Coffee Chats team?</p>
          <p className="mt-1 text-sm" style={{ color: t.body }}>This creates a support ticket and emails the Coffee Chats team.</p>
          <div className="mt-3 flex flex-wrap gap-2">
            <button type="button" disabled={sending || !valid} onClick={send} className={`rounded-full px-4 py-2 text-sm font-semibold disabled:opacity-50 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ backgroundColor: t.primaryBg, color: t.primaryText }}>{sending ? 'Sending…' : 'Confirm and send'}</button>
            <button type="button" disabled={sending} onClick={() => { setConfirming(false); }} className={`rounded-full border px-4 py-2 text-sm font-medium focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ borderColor: t.border, color: t.body }}>Keep editing</button>
          </div>
        </div>
      ) : (
        <button type="button" disabled={!valid} onClick={() => { setConfirming(true); }} className={`mt-4 rounded-full px-4 py-2.5 text-sm font-semibold disabled:opacity-50 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ backgroundColor: t.primaryBg, color: t.primaryText }}>Send to Coffee Chats</button>
      )}
    </div>
  );
}

/** Pulsing ball — mirrors main-client WorkingDot while a turn is in flight. */
function WorkingDot({ label = 'Working' }) {
  const t = useTokens();
  return (
    <span
      className="cc-working-dot inline-block"
      style={{ backgroundColor: t.name === 'console' ? t.heading : '#6F4E37' }}
      role="status"
      aria-label={label}
    />
  );
}

function ThinkingTrace({ item }) {
  const t = useTokens();
  const [open, setOpen] = useState(false);
  const [elapsed, setElapsed] = useState(item.thinkingMs || 0);
  useEffect(() => {
    if (!item.pending) {
      setElapsed((current) => { return item.thinkingMs || current; });
      return undefined;
    }
    const startedAt = item.startedAt || (item.at ? new Date(item.at).getTime() : Date.now());
    const tick = () => { setElapsed(Date.now() - startedAt); };
    tick();
    const timer = setInterval(tick, 250);
    return () => { clearInterval(timer); };
  }, [item.pending, item.startedAt, item.at, item.thinkingMs]);
  const streamedLines = Array.isArray(item.thinkingLines) ? item.thinkingLines.filter(Boolean) : [];
  const timeline = Array.isArray(item.thinking) ? item.thinking.filter(Boolean) : [];
  const settledReasoning = timeline.filter((entry) => { return entry.type !== 'tool'; });
  const timelineTools = timeline.filter((entry) => { return entry.type === 'tool'; });
  const extraTools = Array.isArray(item.steps) ? item.steps.filter(Boolean) : [];
  const reasoning = settledReasoning.length
    ? settledReasoning
    : streamedLines.map((line) => { return { type: 'reasoning', title: line }; });
  const tools = [...timelineTools, ...extraTools.filter((step) => {
    return !timelineTools.some((entry) => { return entry.id && step.id && entry.id === step.id; });
  })];
  const activity = timeline.length
    ? [...timeline, ...extraTools.filter((step) => {
      return !timelineTools.some((entry) => { return entry.id && step.id && entry.id === step.id; });
    })]
    : [...reasoning, ...tools];
  // Queue wait (no startedAt yet) is owned by WorkingDot. Once the worker
  // claims the job (`startedAt` latched) show "Thinking for Xs" even before
  // the first reasoning headline lands.
  const workerStarted = Boolean(item.startedAt || reasoning.length || tools.length || item.thinkingMs > 0);
  if (!workerStarted) { return null; }
  const seconds = Math.max(0, Math.round((item.pending ? elapsed : item.thinkingMs || elapsed) / 1000));
  const latestReasoning = reasoning[reasoning.length - 1];
  const latestTool = tools[tools.length - 1];
  const runningTool = tools.find((tool) => { return tool.status === 'running'; });
  const preview = latestReasoning?.title
    || latestReasoning?.summary
    || runningTool?.label
    || runningTool?.tool
    || latestTool?.label
    || latestTool?.tool
    || '';
  const toolLabel = (tool) => {
    const label = tool.label || tool.title || tool.tool || 'Used a tool';
    return String(label).replace(/_/g, ' ');
  };
  // Live only (main-client parity): once settled, collapsed state is just
  // "Thought for Xs" — the one-liner returns when the user expands the rail.
  const showCollapsedStatus = !open && item.pending && Boolean(preview);
  const hasActivity = reasoning.length > 0 || tools.length > 0;
  return (
    <div className="mb-3 min-w-0">
      <button type="button" aria-expanded={open} aria-label={open ? 'Hide thinking' : 'Show thinking'} onClick={() => { setOpen((value) => { return !value; }); }} className={`flex items-center gap-1.5 rounded-lg py-1 text-xs font-semibold transition-colors hover:text-[#2B1B12] focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.muted }}>
        <span className={item.pending ? 'cc-thinking-glow' : ''}>
          {item.pending ? `Thinking for ${seconds}s` : `Thought for ${seconds}s`}
        </span>
        {hasActivity ? (
          <span className={`cc-ai-chevron inline-flex ${open ? 'cc-ai-chevron-open' : ''}`}>
            <Icon name="chevron" size={13} />
          </span>
        ) : null}
      </button>
      <AnimatedCollapse open={showCollapsedStatus}>
        <p className={`max-w-full truncate text-[13px] leading-5 ${!item.content ? 'cc-thinking-glow' : ''}`} style={{ color: t.muted }}>
          {preview}
        </p>
      </AnimatedCollapse>
      <AnimatedCollapse open={open && hasActivity} className="mt-2">
        <div className="relative ml-1 space-y-3 border-l pl-[18px]" style={{ borderColor: t.border }} aria-label="Thinking activity">
          {activity.map((entry, index) => {
            if (entry.type === 'tool' || entry.tool) {
              const running = entry.status === 'running';
              const failed = entry.status === 'error';
              const query = typeof entry.request?.query === 'string' ? entry.request.query : '';
              const summary = typeof entry.summary === 'string' ? entry.summary : '';
              return (
                <div key={entry.id || `${entry.tool || entry.label}-${index}`} className="flex min-w-0 items-start gap-2" aria-label={toolLabel(entry)}>
                  <span className={`mt-0.5 flex h-4 w-4 shrink-0 items-center justify-center ${running ? 'cc-thinking-glow' : ''}`} style={{ color: failed ? t.danger : t.muted }}><Icon name={failed ? 'x' : (running ? 'sparkles' : 'check')} size={13} /></span>
                  <div className="min-w-0 flex-1">
                    <p className="truncate text-[13px] font-semibold leading-[18px]" style={{ color: failed ? t.danger : t.heading }}>{toolLabel(entry)}{failed ? ' (failed)' : ''}</p>
                    {query || summary ? <p className="truncate text-[12px] leading-5" style={{ color: t.muted, fontFamily: query ? t.fonts.mono : t.fonts.body }}>{query || summary}</p> : null}
                  </div>
                </div>
              );
            }
            const title = entry.title || entry.summary || 'Reasoning';
            const summary = entry.summary && entry.summary !== title ? entry.summary : null;
            return (
              <div key={`${title}-${index}`} className="relative min-w-0">
                <span className="absolute -left-[23px] top-1 h-[9px] w-[9px] rounded-full border-[1.5px]" style={{ borderColor: t.body, backgroundColor: t.page }} />
                <p className="text-[13px] font-semibold leading-[18px]" style={{ color: t.heading }}>{title}</p>
                {summary ? <p className="mt-1 whitespace-pre-wrap text-[13px] leading-5" style={{ color: t.muted }}>{summary}</p> : null}
              </div>
            );
          })}
        </div>
      </AnimatedCollapse>
    </div>
  );
}

function BranchNav({ item, nodes, onNavigate }) {
  const t = useTokens();
  const siblings = branchSiblings(nodes, item);
  if (siblings.length < 2) { return null; }
  const index = Math.max(0, siblings.findIndex((value) => { return value.id === item.id; }));
  return (
    <div className="ml-1 flex items-center gap-0.5" aria-label="Message branches">
      <button type="button" aria-label="Previous branch" disabled={index === 0} onClick={() => { onNavigate(siblings[index - 1].id); }} className={`rounded-md p-1 disabled:opacity-30 focus:outline-none focus-visible:ring-2 ${t.focusRing}`}><Icon name="left" size={14} /></button>
      <span className="min-w-8 text-center text-[11px]" style={{ color: t.muted }}>{index + 1}/{siblings.length}</span>
      <button type="button" aria-label="Next branch" disabled={index === siblings.length - 1} onClick={() => { onNavigate(siblings[index + 1].id); }} className={`rounded-md p-1 disabled:opacity-30 focus:outline-none focus-visible:ring-2 ${t.focusRing}`}><Icon name="right" size={14} /></button>
    </div>
  );
}

function MessageRow({
  item, nodes, busy, onEdit, onFeedback, onRegenerate, onNavigateBranch, onPreviewAttachment,
  submitSupportRequest, enableSupportDraft, getAttachmentUrl,
}) {
  const t = useTokens();
  const assistant = item.role === 'assistant';
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(item.content);
  const [copied, setCopied] = useState(false);
  const copy = async () => {
    await navigator.clipboard?.writeText(item.content || '');
    setCopied(true);
    setTimeout(() => { setCopied(false); }, 1200);
  };
  const actionClass = `rounded-md p-1.5 transition-colors hover:bg-[#F5EBDB] focus:outline-none focus-visible:ring-2 ${t.focusRing}`;
  const actions = item.pending ? null : (
    <div
      className={`mt-1 flex min-h-7 items-center opacity-0 transition-opacity pointer-events-none group-hover:opacity-100 group-hover:pointer-events-auto group-focus-within:opacity-100 group-focus-within:pointer-events-auto [@media(hover:none)]:opacity-100 [@media(hover:none)]:pointer-events-auto ${assistant ? 'justify-start' : 'justify-end'}`}
      style={{ color: t.muted }}
    >
      {!assistant ? <button type="button" aria-label="Edit message" disabled={busy} onClick={() => { setDraft(item.content); setEditing(true); }} className={actionClass}><Icon name="edit" size={14} /></button> : null}
      <button type="button" aria-label="Copy message" onClick={copy} className={actionClass}><Icon name={copied ? 'check' : 'copy'} size={14} /></button>
      {assistant ? <>
        <button type="button" aria-label="Good response" aria-pressed={item.feedback === 'up'} onClick={() => { onFeedback(item.id, item.feedback === 'up' ? null : 'up'); }} className={actionClass} style={item.feedback === 'up' ? { color: '#2B1B12', backgroundColor: '#F5EBDB' } : undefined}><Icon name="thumbsUp" size={14} /></button>
        <button type="button" aria-label="Bad response" aria-pressed={item.feedback === 'down'} onClick={() => { onFeedback(item.id, item.feedback === 'down' ? null : 'down'); }} className={actionClass} style={item.feedback === 'down' ? { color: '#2B1B12', backgroundColor: '#F5EBDB' } : undefined}><Icon name="thumbsDown" size={14} /></button>
        <button type="button" aria-label="Regenerate response" disabled={busy} onClick={() => { onRegenerate(item); }} className={actionClass}><Icon name="refresh" size={14} /></button>
      </> : null}
      <BranchNav item={item} nodes={nodes} onNavigate={onNavigateBranch} />
    </div>
  );

  // User bubble + edit chrome are always the light sand surface (console and
  // portal). Pin espresso ink / sand borders — never theme heading/input tokens,
  // or console's cream text washes out on cream.
  const userBubbleInk = '#2B1B12';
  const userBubbleBg = '#F5EBDB';
  const userBubbleBorder = '#E7DCCB';
  const userBubbleMuted = '#7B665C';
  // Light-surface focus ring (portal clay); console's cream ring reads wrong here.
  const userBubbleFocus = 'focus-visible:ring-[#E14E2C] focus-visible:ring-offset-[#F5EBDB]';

  if (editing) {
    return (
      <div className="flex justify-end">
        <div className="w-full max-w-2xl rounded-2xl p-3" style={{ backgroundColor: userBubbleBg, colorScheme: 'light' }}>
          <textarea
            value={draft}
            onChange={(event) => { setDraft(event.target.value); }}
            rows={3}
            className={`block w-full resize-y rounded-xl border px-3 py-2.5 text-[15px] focus:outline-none focus-visible:ring-2 ${userBubbleFocus}`}
            style={{
              color: userBubbleInk,
              caretColor: userBubbleInk,
              backgroundColor: '#FFFFFF',
              borderColor: userBubbleBorder,
            }}
            aria-label="Edit message text"
          />
          <div className="mt-2 flex justify-end gap-2">
            <button
              type="button"
              onClick={() => { setEditing(false); }}
              className={`rounded-full border px-3 py-1.5 text-xs focus:outline-none focus-visible:ring-2 ${userBubbleFocus}`}
              style={{ color: userBubbleMuted, borderColor: userBubbleBorder, backgroundColor: '#FFFFFF' }}
            >
              Cancel
            </button>
            <button
              type="button"
              disabled={!draft.trim() || draft.trim() === item.content.trim()}
              onClick={() => { onEdit(item, draft.trim(), item.attachments || []); setEditing(false); }}
              className={`rounded-full px-4 py-1.5 text-xs font-semibold disabled:opacity-40 focus:outline-none focus-visible:ring-2 ${userBubbleFocus}`}
              style={{ color: '#FFFBF6', backgroundColor: '#4A2C1D' }}
            >
              Send
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={`group flex ${assistant ? 'justify-start' : 'justify-end'}`}>
      {assistant ? (
        <article className="w-full max-w-3xl">
          <div className="min-w-0">
            <ThinkingTrace item={item} />
            {item.content ? <AssistantMarkdown content={item.content} /> : null}
            {/* Main-client parity: glowing ball while queued (pre-claim) OR while
                answer text is streaming. After claim with no content yet, only
                "Thinking for Xs" shows — no ball under the timer. */}
            {(item.pending && !item.content && !item.startedAt)
              || (item.pending && item.content) ? (
                <WorkingDot label={item.startedAt ? 'Thinking' : 'Starting'} />
              ) : null}
            {item.stopped ? <p className="mt-2 text-xs" style={{ color: t.muted }}>You stopped this response</p> : null}
            {!item.pending && !item.stopped && !(item.content || '').trim() ? (
              // item.aiPaused: the AI kill switch refused this turn (holds the
              // server's sentence), so say that instead of a failed reply.
              <p className="mt-2 flex items-center gap-1.5 text-sm" style={{ color: item.aiPaused ? t.muted : t.accent }} role="status">
                <Icon name="alert" size={13} />
                {item.aiPaused || 'I wasn’t able to generate a response. Please try again.'}
              </p>
            ) : null}
            <SourceLinks sources={item.sources} />
            {item.founderAlertDraft && enableSupportDraft ? <FounderAlertDraft draft={item.founderAlertDraft} submitSupportRequest={submitSupportRequest} /> : null}
            {actions}
          </div>
        </article>
      ) : (
        <div className="flex max-w-[85%] flex-col items-end">
          {item.attachments?.length ? (
            <div className="mb-2 flex max-w-full flex-wrap justify-end gap-2">
              {item.attachments.map((attachment, index) => {
                return <AttachmentChip key={attachment.fileId || index} attachment={attachment} onPreview={onPreviewAttachment} getAttachmentUrl={getAttachmentUrl} />;
              })}
            </div>
          ) : null}
          {item.content ? <article className="rounded-2xl px-4 py-3" style={{ backgroundColor: userBubbleBg, color: userBubbleInk }}><p className="whitespace-pre-wrap text-[15px] leading-6">{item.content}</p></article> : null}
          {actions}
        </div>
      )}
    </div>
  );
}

function HistoryList({ conversations, activeId, onSelect, emptyLabel = 'No previous chats yet.' }) {
  const t = useTokens();
  if (!conversations.length) { return <p className="px-3 py-4 text-sm" style={{ color: t.muted }}>{emptyLabel}</p>; }
  return (
    <div className="flex flex-col gap-0.5">
      {conversations.map((conversation) => {
        const active = conversation.id === activeId;
        const running = Boolean(conversation.running);
        return (
          <button
            key={conversation.id}
            type="button"
            onClick={() => { onSelect(conversation); }}
            aria-label={running ? `${conversation.title} (running)` : conversation.title}
            className={`flex items-center gap-2 rounded-lg px-2 py-2 text-left transition-colors duration-150 focus:outline-none focus-visible:ring-2 ${t.focusRing} ${
              active
                ? 'bg-[rgba(111,78,55,0.12)] hover:bg-[rgba(111,78,55,0.16)]'
                : 'bg-transparent hover:bg-[rgba(111,78,55,0.08)]'
            }`}
          >
            {running ? <Spinner size={14} className="shrink-0" /> : null}
            <span
              className={`min-w-0 flex-1 truncate text-sm ${active ? 'font-semibold' : 'font-medium'}`}
              style={{ color: active ? t.heading : t.muted }}
            >
              {conversation.title}
            </span>
            <span className="shrink-0 text-[11px]" style={{ color: t.muted }}>
              {running ? 'Generating…' : formatChatTime(conversation.updatedAt)}
            </span>
          </button>
        );
      })}
    </div>
  );
}

function Composer({
  value, onChange, onSubmit, onStop, onStopAndSend, loading, mode, onModeChange, compact = false,
  placeholder, addContextOptions, attachments, onPreviewAttachment, enableAttachments = false,
  getAttachmentUrl,
}) {
  const t = useTokens();
  const [plusOpen, setPlusOpen] = useState(false);
  const [modeOpen, setModeOpen] = useState(false);
  const plusRef = useRef(null);
  const modeRef = useRef(null);
  const fileInputRef = useRef(null);
  useDismiss(plusOpen, setPlusOpen, plusRef);
  useDismiss(modeOpen, setModeOpen, modeRef);
  const currentMode = RESPONSE_MODES.find(([valueKey]) => { return valueKey === mode; }) || RESPONSE_MODES[1];

  const keyDown = (event) => {
    if (event.key === 'Enter' && !event.shiftKey) {
      event.preventDefault();
      if (loading) {
        if ((value.trim() || attachments.resolved().length) && !attachments.uploadsPending) { onStopAndSend(); } else { onStop(); }
      } else if (!attachments.uploadsPending) {
        onSubmit();
      }
    }
  };
  const hasDraft = Boolean(value.trim() || attachments.resolved().length);
  const canStopAndSend = hasDraft && !attachments.uploadsPending;
  const sendLabel = loading ? (canStopAndSend ? 'Stop and send' : 'Stop generating') : 'Send message';
  const sendAction = loading ? (canStopAndSend ? onStopAndSend : onStop) : onSubmit;

  const softHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFF9EE]';
  const menuItemHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFFBF6]';
  const composerShadow = t.name === 'console'
    ? 'shadow-[0_8px_28px_rgba(0,0,0,0.28)]'
    : 'shadow-[0_8px_28px_rgba(43,27,18,0.07)]';

  return (
    <div className="w-full">
      <div
        className={`rounded-2xl border ${composerShadow} ${compact ? 'p-2' : 'p-[14px]'}`}
        style={{
          borderColor: t.inputBorder,
          // Console inputBg equals page; lift with surface so the field reads.
          backgroundColor: t.name === 'console' ? t.surface : t.inputBg,
        }}
      >
        {enableAttachments ? <input ref={fileInputRef} type="file" multiple accept={AI_ATTACHMENT_ACCEPT} className="sr-only" aria-label="Choose Coffee Chats AI attachments" onChange={(event) => { attachments.add(event.target.files); event.target.value = ''; }} /> : null}
        {enableAttachments && attachments.chips.length ? (
          <div className="mb-2 flex max-h-[164px] flex-wrap gap-2 overflow-y-auto px-1 pt-1">
            {attachments.chips.map((attachment) => {
              return <AttachmentChip key={attachment.localId} attachment={attachment} onRemove={attachments.remove} onPreview={onPreviewAttachment} getAttachmentUrl={getAttachmentUrl} />;
            })}
          </div>
        ) : null}
        {enableAttachments && attachments.notice ? <p className="mb-2 px-1 text-xs" role="alert" style={{ color: t.danger }}>{attachments.notice}</p> : null}
        <textarea
          value={value}
          onChange={(event) => { onChange(event.target.value); }}
          onKeyDown={keyDown}
          maxLength={4000}
          rows={compact ? 1 : 2}
          placeholder={placeholder}
          className={`block w-full resize-none border-0 bg-transparent text-[15px] leading-6 focus:outline-none ${compact ? 'min-h-[40px] px-2 py-2' : 'h-12 min-h-12 px-1 pb-2 pt-1'}`}
          style={{ color: t.inputText }}
          aria-label="Message Coffee Chats AI"
        />
        <div className={`${compact ? 'mt-1' : ''} flex items-center justify-between gap-3`}>
          <div ref={plusRef} className="relative">
            <button type="button" aria-label="Add context" aria-expanded={plusOpen} onClick={() => { setPlusOpen((open) => { return !open; }); }} className={`flex ${compact ? 'h-9 w-9' : 'h-[34px] w-[34px]'} items-center justify-center rounded-lg ${softHover} focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.heading }}><Icon name="plus" size={20} /></button>
            {plusOpen ? (
              <div className="absolute bottom-full left-0 z-40 mb-2 w-64 rounded-xl border p-1.5 shadow-xl" style={{ backgroundColor: t.surface, borderColor: t.border }}>
                <p className="px-3 py-2 text-[11px] uppercase tracking-wide" style={{ color: t.muted, fontFamily: t.fonts.mono }}>Add context</p>
                {enableAttachments ? <button
                  type="button"
                  disabled={attachments.chips.length >= MAX_AI_ATTACHMENTS}
                  onClick={() => { fileInputRef.current?.click(); setPlusOpen(false); }}
                  className={`flex w-full items-center gap-2 rounded-lg px-3 py-2 text-left text-sm ${menuItemHover} disabled:opacity-45`}
                  style={{ color: t.body }}
                >
                  <Icon name="file" size={15} />{attachments.chips.length >= MAX_AI_ATTACHMENTS ? 'Attachment limit reached' : 'Upload files'}
                </button> : null}
                {(addContextOptions || []).map((label) => {
                  return <button key={label} type="button" onClick={() => { onChange(value ? `${value}\n${label}.` : `${label}.`); setPlusOpen(false); }} className={`w-full rounded-lg px-3 py-2 text-left text-sm ${menuItemHover}`} style={{ color: t.body }}>{label}</button>;
                })}
              </div>
            ) : null}
          </div>
          <div className="flex items-center gap-2">
            <div ref={modeRef} className="relative">
              <button type="button" aria-label={`Response mode: ${currentMode[1]}`} aria-expanded={modeOpen} onClick={() => { setModeOpen((open) => { return !open; }); }} className={`flex ${compact ? 'h-10' : 'h-9'} items-center gap-1.5 rounded-lg px-2.5 text-sm font-semibold ${softHover} focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.body }}>
                <Icon name={currentMode[2]} size={15} /><span>{currentMode[1]}</span><Icon name="chevron" size={14} />
              </button>
              {modeOpen ? (
                <div className="absolute bottom-full right-0 z-40 mb-2 w-48 rounded-xl border p-1.5 shadow-xl" style={{ backgroundColor: t.surface, borderColor: t.border }}>
                  {RESPONSE_MODES.map(([key, label, icon]) => {
                    return (
                      <button key={key} type="button" onClick={() => { onModeChange(key); setModeOpen(false); }} className={`flex w-full items-center justify-between rounded-lg px-3 py-2.5 text-sm ${menuItemHover}`} style={{ color: t.body }}>
                        <span className="flex items-center gap-2"><Icon name={icon} size={15} />{label}</span>{mode === key ? <Icon name="check" size={15} /> : null}
                      </button>
                    );
                  })}
                </div>
              ) : null}
            </div>
            <button type="button" onClick={sendAction} disabled={!loading && (!hasDraft || attachments.uploadsPending)} className={`flex h-10 w-10 items-center justify-center rounded-[10px] disabled:opacity-35 focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ backgroundColor: t.primaryBg, color: t.primaryText }} aria-label={sendLabel}><Icon name={loading ? 'square' : 'arrow'} size={18} /></button>
          </div>
        </div>
      </div>
    </div>
  );
}

export function AiChatSkeleton({ identityLabel = 'Coffee Chats AI', layoutClassName = 'h-[calc(100dvh-88px)] min-[900px]:h-[100dvh]' }) {
  const t = useTokens();
  const block = { backgroundColor: t.hairline };
  return (
    <section className={`flex ${layoutClassName} min-h-0 animate-pulse flex-col overflow-hidden`} style={{ backgroundColor: t.page }} aria-label="Loading Coffee Chats AI">
      <div className="flex min-h-[70px] items-center justify-between px-4 py-3 sm:px-6 lg:px-8">
        <div className="h-9 w-28 rounded-lg" style={block} />
        <div className="h-9 w-9 rounded-lg" style={block} />
      </div>
      <div className="mx-auto flex w-full max-w-[1080px] flex-1 flex-col px-4 pb-8 sm:px-6">
        <div className="min-h-[70px] flex-[0.72]" />
        <div className="mx-auto flex w-full max-w-[720px] flex-col items-center">
          <div className="mb-5 flex items-center gap-3"><div className="h-[42px] w-[42px] rounded-xl" style={block} /><span className="sr-only">{identityLabel}</span><div className="h-5 w-40 rounded" style={block} /></div>
          <div className="mb-6 h-10 w-80 max-w-[80vw] rounded-lg" style={block} />
          <div className="h-[112px] w-full rounded-2xl border" style={{ backgroundColor: t.surface, borderColor: t.border }} />
          <div className="mt-4 flex flex-wrap justify-center gap-2">{[118, 86, 90, 112].map((width) => { return <div key={width} className="h-[42px] rounded-xl" style={{ ...block, width }} />; })}</div>
        </div>
        <div className="min-h-[88px] flex-1 pt-8"><div className="mx-auto w-full max-w-[720px]"><div className="mb-3 h-4 w-72 rounded" style={block} /><div className="grid grid-cols-1 gap-3 sm:grid-cols-3">{[0, 1, 2].map((item) => { return <div key={item} className="h-[150px] rounded-xl" style={{ backgroundColor: t.statBg }} />; })}</div></div></div>
      </div>
    </section>
  );
}

export default function AiChatShell({
  storageKey,
  modeKey = 'cc.ai.response-mode',
  quickActions = INSTITUTION_QUICK_ACTIONS,
  projectCards = INSTITUTION_PROJECT_CARDS,
  composerPlaceholder = 'Ask about your program',
  addContextOptions = ['Focus on analytics', 'Focus on seats and billing', 'Include privacy constraints'],
  emptySectionTitle = 'Use Coffee Chats AI to run your institution program',
  disclaimer = 'Coffee Chats AI can make mistakes. Privacy Policy and Terms links are authoritative.',
  profile,
  streamMessage,
  submitSupportRequest = null,
  enableSupportDraft = Boolean(submitSupportRequest),
  enableAttachments = false,
  uploadAttachment = uploadInstitutionAiAttachment,
  getAttachmentUrl = getInstitutionAiAttachmentUrl,
  initialConversationId = null,
  onConversationChange = null,
  listConversations: listPersistedConversations = null,
  createConversation = null,
  loadConversation = null,
  reattachTurn = null,
  cancelTurn = null,
  renderIdentity,
  layoutClassName = 'h-[calc(100dvh-88px)] min-[900px]:h-[100dvh]',
}) {
  const t = useTokens();
  const [nodes, setNodes] = useState([]);
  const [activeLeafId, setActiveLeafId] = useState(null);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeConversationId, setActiveConversationId] = useState(initialConversationId);
  const [conversations, setConversations] = useState(() => { return readHistory(storageKey); });
  const [historyOpen, setHistoryOpen] = useState(false);
  const [inlineHistoryOpen, setInlineHistoryOpen] = useState(false);
  const [quickOpen, setQuickOpen] = useState(null);
  const [previewAttachment, setPreviewAttachment] = useState(null);
  const attachmentState = useAiAttachments({ upload: uploadAttachment });
  const [mode, setMode] = useState(() => {
    try { return window.localStorage.getItem(modeKey) || 'balanced'; } catch { return 'balanced'; }
  });
  const historyRef = useRef(null);
  const quickRef = useRef(null);
  const abortRef = useRef(null);
  const conversationScrollRef = useRef(null);
  const followLatestRef = useRef(true);
  const activeTurnRef = useRef(null);
  const activeConversationIdRef = useRef(activeConversationId);
  const activeLeafIdRef = useRef(activeLeafId);
  const nodesRef = useRef(nodes);
  const conversationsRef = useRef(conversations);
  // Assistant ids the AI kill switch refused this session -> server sentence.
  const aiPausedIdsRef = useRef(new Map());
  useEffect(() => { nodesRef.current = nodes; }, [nodes]);
  useEffect(() => { activeConversationIdRef.current = activeConversationId; }, [activeConversationId]);
  useEffect(() => { activeLeafIdRef.current = activeLeafId; }, [activeLeafId]);
  useEffect(() => { conversationsRef.current = conversations; }, [conversations]);
  useDismiss(historyOpen, setHistoryOpen, historyRef);

  const adoptServerConversation = useCallback((conversation, options = {}) => {
    if (!conversation) { return null; }
    const withLocal = options.mergeLocal
      ? mergeStreamedContentIntoConversation(conversation, nodesRef.current)
      : conversation;
    // Turns the kill switch refused this session keep their paused notice
    // even when the stored node's error text does not carry the code.
    const nextNodes = serverConversationNodes(withLocal).map((node) => {
      const paused = !node.aiPaused && aiPausedIdsRef.current.get(node.id);
      return paused ? { ...node, aiPaused: paused } : node;
    });
    // Never wipe an on-screen transcript when the server snapshot is empty.
    if (!nextNodes.length) { return null; }
    let nextLeafId = withLocal.activeLeafId ? String(withLocal.activeLeafId) : null;
    if (!nextLeafId || !nextNodes.some((node) => { return node.id === nextLeafId; })) {
      nextLeafId = nextNodes[nextNodes.length - 1]?.id || null;
    }
    nodesRef.current = nextNodes;
    activeLeafIdRef.current = nextLeafId;
    followLatestRef.current = true;
    setNodes(nextNodes);
    setActiveLeafId(nextLeafId);
    const conversationId = String(withLocal._id || withLocal.id);
    activeConversationIdRef.current = conversationId;
    setActiveConversationId(conversationId);
    return { nodes: nextNodes, leafId: nextLeafId, conversationId };
  }, []);

  const refreshPersistedHistory = useCallback(async () => {
    if (!listPersistedConversations) { return; }
    try {
      const result = await listPersistedConversations();
      const next = (result?.conversations || []).map((conversation) => {
        return {
          ...conversation,
          id: String(conversation._id || conversation.id),
        };
      });
      setConversations((current) => {
        const localById = new Map(current.map((conversation) => { return [String(conversation.id), conversation]; }));
        const merged = next.map((serverConversation) => {
          const local = localById.get(serverConversation.id);
          return {
            ...local,
            ...serverConversation,
            // Keep a local transcript cache for settled resumes; server owns `running`.
            nodes: local?.nodes,
            messages: local?.messages,
            activeLeafId: local?.activeLeafId || serverConversation.activeLeafId,
            running: Boolean(serverConversation.running),
          };
        });
        const serverIds = new Set(next.map((conversation) => { return conversation.id; }));
        const localOnly = current.filter((conversation) => { return !serverIds.has(String(conversation.id)); });
        return [...merged, ...localOnly].slice(0, HISTORY_LIMIT);
      });
    } catch {
      // The active conversation remains usable when the optional history list fails.
    }
  }, [listPersistedConversations]);

  const loadSettledConversation = useCallback(async (conversationId, assistantMessageId = null) => {
    if (!loadConversation) { return null; }
    let result = null;
    for (const delay of SETTLE_RETRY_DELAYS_MS) {
      if (delay) {
        await new Promise((resolve) => { setTimeout(resolve, delay); });
      }
      try {
        result = await loadConversation(conversationId);
      } catch (error) {
        // A streamed reply can finish before/without a durable row the GET can
        // see (404). Keep the on-screen transcript — never toast settle misses.
        if (error?.response?.status === 404) { return null; }
        throw error;
      }
      // Missing conversation: don't burn the full retry budget (tests + sync path).
      if (!result?.conversation) { return result; }
      if (conversationIsSettled(result.conversation, assistantMessageId)) { return result; }
    }
    return result;
  }, [loadConversation]);

  const persistAdoptedConversation = useCallback((adopted, running = false) => {
    if (!adopted) { return; }
    const activePath = pathToLeaf(adopted.nodes, adopted.leafId);
    const firstUser = activePath.find((item) => { return item.role === 'user'; });
    if (!firstUser) { return; }
    const attachmentTitle = firstUser.attachments?.map((attachment) => {
      return attachment.originalFilename || attachment.name;
    }).filter(Boolean).join(', ');
    const titleSource = firstUser.content || attachmentTitle || 'Attached files';
    const id = adopted.conversationId;
    const entry = {
      id,
      title: titleSource.length > 54 ? `${titleSource.slice(0, 54)}…` : titleSource,
      nodes: adopted.nodes,
      activeLeafId: adopted.leafId,
      messages: activePath,
      updatedAt: new Date().toISOString(),
      running: Boolean(running),
    };
    setConversations((current) => {
      const next = [entry, ...current.filter((item) => { return item.id !== id; })].slice(0, HISTORY_LIMIT);
      writeHistory(storageKey, next);
      return next;
    });
  }, [storageKey]);

  // Keep the streamed in-memory node visible while polling — never adopt a
  // half-written `status:'streaming'` snapshot mid-loop (that re-lights
  // "Thinking forever"). On the last attempt, force every streaming node
  // terminal (and keep streamed text) so a stuck worker can't empty the UI.
  const reloadUntilTurnSettled = useCallback(async (conversationId, assistantMessageId) => {
    if (!loadConversation || !conversationId) { return null; }
    const aid = assistantMessageId ? String(assistantMessageId) : null;
    let result = null;
    for (let attempt = 0; attempt < RELOAD_SETTLE_ATTEMPTS; attempt += 1) {
      try {
        result = await loadConversation(conversationId);
      } catch {
        if (attempt === RELOAD_SETTLE_ATTEMPTS - 1) { return result; }
        await new Promise((resolve) => { setTimeout(resolve, RELOAD_SETTLE_INTERVAL_MS); });
        continue;
      }
      const settled = conversationIsSettled(result?.conversation, aid);
      if (settled) {
        const adopted = adoptServerConversation(result.conversation, { mergeLocal: true });
        persistAdoptedConversation(adopted, false);
        return result;
      }
      if (attempt === RELOAD_SETTLE_ATTEMPTS - 1) {
        if (result?.conversation) {
          const adopted = adoptServerConversation(
            forceTerminalConversation(result.conversation, nodesRef.current),
            { mergeLocal: true },
          );
          persistAdoptedConversation(adopted, false);
        }
        return result;
      }
      await new Promise((resolve) => { setTimeout(resolve, RELOAD_SETTLE_INTERVAL_MS); });
    }
    return result;
  }, [adoptServerConversation, loadConversation, persistAdoptedConversation]);

  useEffect(() => {
    refreshPersistedHistory();
  }, [refreshPersistedHistory]);

  useEffect(() => {
    const anyRunning = conversations.some((conversation) => { return conversation.running; });
    if (!anyRunning || !listPersistedConversations) { return undefined; }
    const id = window.setInterval(() => {
      refreshPersistedHistory();
    }, RUNNING_HISTORY_POLL_MS);
    return () => { window.clearInterval(id); };
  }, [conversations, listPersistedConversations, refreshPersistedHistory]);

  useEffect(() => {
    if (!loadConversation) { return undefined; }
    if (!initialConversationId) {
      if (!activeTurnRef.current) {
        nodesRef.current = [];
        setNodes([]);
        setActiveLeafId(null);
        activeConversationIdRef.current = null;
        setActiveConversationId(null);
      }
      return undefined;
    }
    if (activeTurnRef.current && String(activeConversationIdRef.current) === String(initialConversationId)) {
      return undefined;
    }
    // Settled local resume already painted this conversation; don't refetch / flash loading.
    // In-flight chats must still load so we can reattach the pending turn.
    // Also re-load when activeLeafId points at a missing node (empty-thread flash).
    const paintedNodes = nodesRef.current;
    const leafId = activeLeafIdRef.current ? String(activeLeafIdRef.current) : null;
    const leafResolves = !leafId || paintedNodes.some((node) => { return node.id === leafId; });
    if (
      String(activeConversationIdRef.current) === String(initialConversationId)
      && paintedNodes.length > 0
      && leafResolves
      && pathToLeaf(paintedNodes, leafId || paintedNodes[paintedNodes.length - 1].id).length > 0
      && !paintedNodes.some((node) => { return node.pending; })
    ) {
      return undefined;
    }
    let active = true;
    const controller = new AbortController();
    (async () => {
      try {
        setLoading(true);
        const result = await loadConversation(initialConversationId);
        if (!active) { return; }
        if (!result?.conversation) {
          // Durable id in the URL but no server row — fall back to local cache
          // (same as a 404) instead of leaving an empty landing flash.
          const err = new Error('Conversation not found');
          err.response = { status: 404, data: { error: 'Conversation not found' } };
          throw err;
        }
        const adopted = adoptServerConversation(result.conversation, { mergeLocal: true });
        const pending = (result.conversation.pendingTurns || []).slice(-1)[0];
        if (!pending?.turnId || !reattachTurn) {
          if (adopted && conversationIsSettled(result.conversation)) {
            persistAdoptedConversation(adopted, false);
          }
          return;
        }
        const assistantId = String(pending.assistantMessageId || '');
        const turn = {
          controller,
          assistantId,
          turnId: pending.turnId,
          conversationId: String(initialConversationId),
          discard: false,
          promise: null,
        };
        activeTurnRef.current = turn;
        abortRef.current = controller;
        const latchThinkingStart = (node) => {
          if (node?.startedAt) { return node; }
          return { ...node, startedAt: Date.now() };
        };
        const updateReattached = (updater) => {
          const next = nodesRef.current.map((node) => { return node.id === assistantId ? updater(node) : node; });
          nodesRef.current = next;
          setNodes(next);
        };
        // Reattach joins an already-claimed worker turn — start the timer now
        // (same as main client), don't show the queue ball.
        updateReattached((node) => { return latchThinkingStart({ ...node, pending: true }); });
        await reattachTurn({
          conversationId: initialConversationId,
          turnId: pending.turnId,
          signal: controller.signal,
          onStatus: (payload) => {
            if (payload?.phase !== 'started') { return; }
            updateReattached((node) => { return latchThinkingStart(node); });
          },
          onThinking: (line) => {
            updateReattached((node) => {
              const next = latchThinkingStart(node);
              const lines = next.thinkingLines || [];
              return lines.includes(line) ? next : { ...next, thinkingLines: [...lines, line], pending: true };
            });
          },
          onDelta: (delta) => {
            updateReattached((node) => {
              const next = latchThinkingStart(node);
              return { ...next, content: `${next.content || ''}${delta}`, pending: true };
            });
          },
        });
        if (!active) { return; }
        // Worker reattach often ends with `{ reload: true }` before Mongo
        // finalize lands — poll until the node leaves `streaming`. Never adopt
        // a half-written snapshot (that re-lights "Thinking forever").
        if (active) { await reloadUntilTurnSettled(initialConversationId, assistantId); }
      } catch (error) {
        if (active && error?.name !== 'AbortError') {
          // Prefer localStorage (writeHistory is sync) over conversationsRef —
          // the ref can lag one render behind saveConversation after a turn.
          const local = readHistory(storageKey).find((conversation) => {
            return String(conversation.id) === String(initialConversationId);
          }) || conversationsRef.current.find((conversation) => {
            return String(conversation.id) === String(initialConversationId);
          });
          const localNodes = conversationNodes(local);
          if (error?.response?.status === 404 && localNodes.length) {
            // Orphaned / local-only cache: reopen from history instead of toasting.
            const nextNodes = normalizeNodes(localNodes);
            followLatestRef.current = true;
            nodesRef.current = nextNodes;
            let leafId = local?.activeLeafId || null;
            if (!leafId || !nextNodes.some((node) => { return node.id === leafId; })) {
              leafId = nextNodes[nextNodes.length - 1]?.id || null;
            }
            activeLeafIdRef.current = leafId;
            setNodes(nextNodes);
            setActiveLeafId(leafId);
            activeConversationIdRef.current = String(local.id);
            setActiveConversationId(String(local.id));
            return;
          }
          if (error?.response?.status === 404) {
            onConversationChange?.(null, { replace: true });
          }
          notifyRequestError(error, 'Could not reopen this chat.');
        }
      } finally {
        if (activeTurnRef.current?.controller === controller) { activeTurnRef.current = null; }
        if (abortRef.current === controller) { abortRef.current = null; }
        if (active) { setLoading(false); }
      }
    })();
    return () => {
      active = false;
      controller.abort();
    };
  }, [adoptServerConversation, initialConversationId, loadConversation, onConversationChange,
    persistAdoptedConversation, reattachTurn, reloadUntilTurnSettled, storageKey]);

  const messages = useMemo(() => { return pathToLeaf(nodes, activeLeafId); }, [activeLeafId, nodes]);
  useEffect(() => {
    const scroller = conversationScrollRef.current;
    if (!scroller || !messages.length || !followLatestRef.current) { return; }
    scroller.scrollTop = scroller.scrollHeight;
  }, [messages]);

  useEffect(() => {
    if (!quickOpen) { return undefined; }
    const outside = (event) => { if (!quickRef.current?.contains(event.target)) { setQuickOpen(null); } };
    document.addEventListener('pointerdown', outside);
    return () => { document.removeEventListener('pointerdown', outside); };
  }, [quickOpen]);

  const saveConversation = useCallback((nextNodes, nextLeafId, forcedId, options = {}) => {
    const activePath = pathToLeaf(nextNodes, nextLeafId);
    const firstUser = activePath.find((item) => { return item.role === 'user'; });
    if (!firstUser) { return null; }
    const attachmentTitle = firstUser.attachments?.map((attachment) => {
      return attachment.originalFilename || attachment.name;
    }).filter(Boolean).join(', ');
    const titleSource = firstUser.content || attachmentTitle || 'Attached files';
    const id = forcedId || activeConversationIdRef.current || `institution-chat-${Date.now()}`;
    const entry = {
      id,
      title: titleSource.length > 54 ? `${titleSource.slice(0, 54)}…` : titleSource,
      nodes: nextNodes,
      activeLeafId: nextLeafId,
      // Keep the active path for backward compatibility with existing history.
      messages: activePath,
      updatedAt: new Date().toISOString(),
      running: Boolean(options.running),
    };
    setConversations((current) => {
      const next = [entry, ...current.filter((item) => { return item.id !== id; })].slice(0, HISTORY_LIMIT);
      writeHistory(storageKey, next);
      return next;
    });
    activeConversationIdRef.current = id;
    setActiveConversationId(id);
    return id;
  }, [storageKey]);

  const startNewChat = useCallback(() => {
    const cached = conversationsRef.current.find((conversation) => {
      return String(conversation.id) === String(activeConversationIdRef.current);
    });
    const wasRunning = Boolean(activeTurnRef.current)
      || Boolean(cached?.running)
      || nodes.some((node) => { return node.pending; });
    if (nodes.length) { saveConversation(nodes, activeLeafId, null, { running: wasRunning }); }
    if (activeTurnRef.current) { activeTurnRef.current.discard = true; }
    // Abort the local stream listener only — leave the server turn running so
    // the landing resume card / history can keep showing a spinner until settle.
    abortRef.current?.abort();
    setNodes([]);
    setActiveLeafId(null);
    setInput('');
    attachmentState.clear();
    setPreviewAttachment(null);
    activeConversationIdRef.current = null;
    setActiveConversationId(null);
    setHistoryOpen(false);
    onConversationChange?.(null);
  }, [activeLeafId, attachmentState, nodes, onConversationChange, saveConversation]);

  const openConversationFromServer = (conversationId) => {
    if (activeTurnRef.current) { activeTurnRef.current.discard = true; }
    abortRef.current?.abort();
    setHistoryOpen(false);
    setInlineHistoryOpen(false);
    onConversationChange?.(conversationId);
  };

  const loadConversationIntoView = useCallback(async (conversationId) => {
    if (!loadConversation || !conversationId) { return; }
    if (activeTurnRef.current) { activeTurnRef.current.discard = true; }
    abortRef.current?.abort();
    setHistoryOpen(false);
    setInlineHistoryOpen(false);
    setLoading(true);
    try {
      const result = await loadConversation(conversationId);
      const pending = (result?.conversation?.pendingTurns || []).slice(-1)[0];
      if (pending?.turnId && reattachTurn) {
        const assistantId = String(pending.assistantMessageId || '');
        adoptServerConversation(result.conversation, { mergeLocal: true });
        const controller = new AbortController();
        const turn = {
          controller,
          assistantId,
          turnId: pending.turnId,
          conversationId: String(conversationId),
          discard: false,
          promise: null,
        };
        activeTurnRef.current = turn;
        abortRef.current = controller;
        const latchThinkingStart = (node) => {
          if (node?.startedAt) { return node; }
          return { ...node, startedAt: Date.now() };
        };
        const updateReattached = (updater) => {
          const next = nodesRef.current.map((node) => { return node.id === assistantId ? updater(node) : node; });
          nodesRef.current = next;
          setNodes(next);
        };
        updateReattached((node) => { return latchThinkingStart({ ...node, pending: true }); });
        try {
          await reattachTurn({
            conversationId,
            turnId: pending.turnId,
            signal: controller.signal,
            onStatus: (payload) => {
              if (payload?.phase !== 'started') { return; }
              updateReattached((node) => { return latchThinkingStart(node); });
            },
            onThinking: (line) => {
              updateReattached((node) => {
                const next = latchThinkingStart(node);
                const lines = next.thinkingLines || [];
                return lines.includes(line) ? next : { ...next, thinkingLines: [...lines, line], pending: true };
              });
            },
            onDelta: (delta) => {
              updateReattached((node) => {
                const next = latchThinkingStart(node);
                return { ...next, content: `${next.content || ''}${delta}`, pending: true };
              });
            },
          });
          await reloadUntilTurnSettled(conversationId, assistantId);
        } finally {
          if (activeTurnRef.current === turn) { activeTurnRef.current = null; }
          if (abortRef.current === controller) { abortRef.current = null; }
        }
        return;
      }
      if (conversationIsSettled(result?.conversation)) {
        const adopted = adoptServerConversation(result.conversation, { mergeLocal: true });
        persistAdoptedConversation(adopted, false);
        return;
      }
      await reloadUntilTurnSettled(conversationId, pending?.assistantMessageId || null);
    } catch (error) {
      const local = conversationsRef.current.find((conversation) => {
        return String(conversation.id) === String(conversationId);
      });
      const localNodes = normalizeNodes(conversationNodes(local));
      if (localNodes.length) {
        followLatestRef.current = true;
        nodesRef.current = localNodes;
        setNodes(localNodes);
        const leafId = local?.activeLeafId && localNodes.some((node) => { return node.id === local.activeLeafId; })
          ? local.activeLeafId
          : localNodes[localNodes.length - 1]?.id || null;
        setActiveLeafId(leafId);
        activeConversationIdRef.current = String(conversationId);
        setActiveConversationId(String(conversationId));
        return;
      }
      notifyRequestError(error, 'Could not reopen this chat.');
    } finally {
      setLoading(false);
    }
  }, [adoptServerConversation, loadConversation, persistAdoptedConversation, reattachTurn,
    reloadUntilTurnSettled]);

  const selectConversation = (conversation) => {
    const localNodes = conversationNodes(conversation);
    const alreadyOpen = String(initialConversationId) === String(conversation.id)
      || String(activeConversationIdRef.current) === String(conversation.id);
    const needsServer = Boolean(loadConversation)
      && isDurableConversationId(conversation.id)
      && (conversationLooksInFlight(conversation) || localNodes.length === 0);
    if (needsServer) {
      // Same URL + in-flight/empty local cache used to no-op navigate. Force a load.
      if (alreadyOpen) {
        loadConversationIntoView(conversation.id);
        return;
      }
      openConversationFromServer(conversation.id);
      return;
    }
    if (nodes.length && conversation.id !== activeConversationId) { saveConversation(nodes, activeLeafId); }
    if (activeTurnRef.current) { activeTurnRef.current.discard = true; }
    abortRef.current?.abort();
    const nextNodes = normalizeNodes(localNodes);
    if (!nextNodes.length && isDurableConversationId(conversation.id)) {
      if (alreadyOpen) {
        loadConversationIntoView(conversation.id);
        return;
      }
      openConversationFromServer(conversation.id);
      return;
    }
    followLatestRef.current = true;
    setNodes(nextNodes);
    let nextLeafId = conversation.activeLeafId || null;
    if (!nextLeafId || !nextNodes.some((node) => { return node.id === nextLeafId; })) {
      nextLeafId = nextNodes[nextNodes.length - 1]?.id || null;
    }
    setActiveLeafId(nextLeafId);
    activeConversationIdRef.current = conversation.id;
    setActiveConversationId(conversation.id);
    setInput('');
    attachmentState.clear();
    setPreviewAttachment(null);
    setHistoryOpen(false);
    setInlineHistoryOpen(false);
    // Keep the address bar in sync for durable settled resumes.
    if (isDurableConversationId(conversation.id)) { onConversationChange?.(conversation.id); }
  };

  const runTurn = async ({
    value,
    parentId = activeLeafId,
    includeUser = true,
    restoreInput = includeUser,
    fileAttachments = attachmentState.resolved(),
    consumeComposerAttachments = includeUser,
  }) => {
    const content = value.trim();
    const submittedAttachments = Array.isArray(fileAttachments) ? fileAttachments : [];
    if ((!content && !submittedAttachments.length) || activeTurnRef.current || attachmentState.uploadsPending) { return false; }
    const baseNodes = nodesRef.current;
    const previousLeafId = activeLeafId;
    const timestamp = Date.now();
    const userMessage = includeUser ? {
      id: `user-${timestamp}-${Math.random().toString(36).slice(2, 7)}`,
      parentId: parentId || null,
      role: 'user',
      content,
      attachments: submittedAttachments.length ? submittedAttachments : undefined,
      at: new Date(timestamp).toISOString(),
    } : null;
    const assistantMessage = {
      id: `assistant-${timestamp}-${Math.random().toString(36).slice(2, 7)}`,
      parentId: userMessage?.id || parentId,
      role: 'assistant',
      content: '',
      thinkingLines: [],
      pending: true,
      // Latched when the worker claims the job (`status:started`) or the first
      // thinking/delta arrives — queue wait is glowing-ball only.
      startedAt: null,
      at: new Date(timestamp + 1).toISOString(),
    };
    const latchThinkingStart = (node) => {
      if (node?.startedAt) { return node; }
      return { ...node, startedAt: Date.now() };
    };
    let liveNodes = [...baseNodes, ...(userMessage ? [userMessage] : []), assistantMessage];
    const updateAssistant = (updater) => {
      liveNodes = liveNodes.map((node) => { return node.id === assistantMessage.id ? updater(node) : node; });
      nodesRef.current = liveNodes;
      setNodes(liveNodes);
    };
    const historyPath = pathToLeaf(baseNodes, parentId);
    const requestHistory = (includeUser ? historyPath : historyPath.slice(0, -1))
      .filter((item) => { return !item.pending && (item.content || item.attachments?.length); })
      .map((item) => {
        const breadcrumb = item.role === 'user' ? attachmentBreadcrumb(item) : '';
        return { role: item.role, content: `${item.content || ''}${breadcrumb}`.trim() };
      });
    nodesRef.current = liveNodes;
    followLatestRef.current = true;
    setNodes(liveNodes);
    setActiveLeafId(assistantMessage.id);
    if (includeUser) { setInput(''); }
    if (consumeComposerAttachments) { attachmentState.clear(); }
    setLoading(true);
    setQuickOpen(null);
    const controller = new AbortController();
    const turn = {
      controller,
      assistantId: assistantMessage.id,
      conversationId: activeConversationIdRef.current,
      turnId: null,
      discard: false,
      promise: null,
    };
    activeTurnRef.current = turn;
    abortRef.current = controller;
    try {
      if (!turn.conversationId && createConversation) {
        const attachmentTitle = submittedAttachments
          .map((attachment) => { return attachment.originalFilename || attachment.name; })
          .filter(Boolean)
          .join(', ');
        const created = await createConversation({
          title: content || attachmentTitle || 'Attached files',
          signal: controller.signal,
        });
        const createdId = created?.conversation?._id || created?.conversation?.id;
        if (!createdId) { throw new Error('Could not start a durable conversation.'); }
        turn.conversationId = String(createdId);
        activeConversationIdRef.current = String(createdId);
        setActiveConversationId(String(createdId));
        saveConversation(liveNodes, assistantMessage.id, String(createdId), { running: true });
        onConversationChange?.(String(createdId), { replace: true });
      }
      const result = await streamMessage({
        message: content,
        history: requestHistory,
        mode,
        attachmentIds: submittedAttachments.map((attachment) => { return attachment.fileId; }).filter(Boolean),
        conversationId: turn.conversationId || activeConversationIdRef.current,
        parentId,
        signal: controller.signal,
        onTurn: (payload) => {
          if (!payload?.conversationId) { return; }
          turn.conversationId = String(payload.conversationId);
          turn.turnId = payload.turnId || null;
          turn.assistantMessageId = payload.assistantMessageId || null;
          activeConversationIdRef.current = String(payload.conversationId);
          setActiveConversationId(String(payload.conversationId));
          saveConversation(liveNodes, assistantMessage.id, String(payload.conversationId), { running: true });
          onConversationChange?.(String(payload.conversationId), { replace: true });
        },
        onStatus: (payload) => {
          if (payload?.phase !== 'started') { return; }
          updateAssistant((node) => { return latchThinkingStart(node); });
        },
        onThinking: (line) => {
          updateAssistant((node) => {
            const next = latchThinkingStart(node);
            const lines = next.thinkingLines || [];
            return lines.includes(line) ? next : { ...next, thinkingLines: [...lines, line] };
          });
        },
        onDelta: (delta) => {
          updateAssistant((node) => {
            const next = latchThinkingStart(node);
            const origin = next.startedAt || timestamp;
            const thinkingMs = next.thinkingMs ?? ((next.thinkingLines || []).length ? Date.now() - origin : undefined);
            return { ...next, content: `${next.content || ''}${delta}`, thinkingMs };
          });
        },
      });
      const conversationId = result.conversationId || turn.conversationId
        ? String(result.conversationId || turn.conversationId)
        : null;
      const assistantId = String(turn.assistantMessageId || turn.assistantId || assistantMessage.id);
      const finalAnswer = result.answer || result.content || '';

      // Worker bridge can close with `{ reload: true }` while the assistant
      // node is still `streaming` in Mongo. Poll until finalize lands — do not
      // adopt an unsettled snapshot (re-sets pending and spins forever).
      if (result.reload && loadConversation && conversationId) {
        await reloadUntilTurnSettled(conversationId, assistantId);
        await refreshPersistedHistory();
      } else {
        updateAssistant((node) => {
          return {
            ...node,
            content: finalAnswer || node.content,
            sources: result.sources || [],
            founderAlertDraft: result.founderAlertDraft || null,
            thinking: result.thinking,
            thinkingMs: result.thinkingMs ?? node.thinkingMs,
            pending: false,
          };
        });
        if (loadConversation && conversationId) {
          const persisted = await loadSettledConversation(conversationId, assistantId);
          if (persisted?.conversation && conversationIsSettled(persisted.conversation, assistantId)) {
            const adopted = adoptServerConversation(persisted.conversation, { mergeLocal: true });
            persistAdoptedConversation(adopted, false);
          } else if (persisted?.conversation) {
            await reloadUntilTurnSettled(conversationId, assistantId);
          } else {
            // Keep the streamed transcript if the settle fetch returned nothing.
            saveConversation(nodesRef.current, assistantId, conversationId, { running: false });
          }
          await refreshPersistedHistory();
        } else {
          saveConversation(liveNodes, assistantMessage.id);
        }
      }
    } catch (requestError) {
      if (controller.signal.aborted || requestError?.name === 'AbortError') {
        if (!turn.discard) {
          updateAssistant((node) => {
            return {
              ...node,
              pending: false,
              stopped: true,
              thinkingMs: node.thinkingMs ?? (node.startedAt ? Date.now() - node.startedAt : undefined),
            };
          });
          saveConversation(liveNodes, assistantMessage.id);
        }
      } else if (turn.conversationId || requestError?.midStream) {
        // Main-client parity: a mid-stream / first-token stall keeps the chat
        // (user bubble stays) and settles into the retryable empty-reply
        // placeholder — never wipe the turn or shove the prompt back as unsent.
        // Durable worker turns may still finalize after the bridge gives up.
        const streamedContent = (nodesRef.current.find((node) => {
          return node.id === assistantMessage.id;
        })?.content || '').trim();
        // AI kill switch: the turn settles into the paused notice (the
        // server's sentence) instead of the failed-reply placeholder.
        const paused = isAiPausedError(requestError) ? aiPausedMessage(requestError) : null;
        if (paused && turn.assistantMessageId) {
          aiPausedIdsRef.current.set(String(turn.assistantMessageId), paused);
        }
        // Toast only when the model never answered. Post-reply settle 404s must
        // not flash "Conversation not found" over a finished transcript. A
        // paused turn already says why inline, so it gets no second toast.
        if (!streamedContent && !paused) {
          notifyRequestError(requestError, 'Coffee Chats AI could not answer right now. Please try again.');
        }
        updateAssistant((node) => {
          return {
            ...node,
            pending: false,
            errored: !streamedContent,
            aiPaused: (!streamedContent && paused) || undefined,
            thinkingMs: node.thinkingMs ?? (node.startedAt ? Date.now() - node.startedAt : undefined),
          };
        });
        const conversationId = turn.conversationId
          ? String(turn.conversationId)
          : null;
        const assistantId = String(turn.assistantMessageId || turn.assistantId || assistantMessage.id);
        if (conversationId) {
          saveConversation(nodesRef.current, assistantId, conversationId, { running: false });
          // Poll only when the server actually started a durable turn — a bare
          // createConversation shell + pre-stream failure has nothing to settle.
          if (loadConversation && (turn.turnId || turn.assistantMessageId) && !streamedContent) {
            await reloadUntilTurnSettled(conversationId, assistantId);
          }
          await refreshPersistedHistory();
        } else {
          saveConversation(liveNodes, assistantMessage.id);
        }
      } else {
        nodesRef.current = baseNodes;
        setNodes(baseNodes);
        setActiveLeafId(previousLeafId);
        if (restoreInput) { setInput(content); }
        if (consumeComposerAttachments) { attachmentState.reset(submittedAttachments); }
        notifyRequestError(requestError, 'Coffee Chats AI could not answer right now. Please try again.');
      }
    } finally {
      if (abortRef.current === controller) { abortRef.current = null; }
      if (activeTurnRef.current === turn) {
        activeTurnRef.current = null;
        setLoading(false);
      }
    }
    return true;
  };

  const startTurn = (options) => {
    const promise = runTurn(options);
    if (activeTurnRef.current) { activeTurnRef.current.promise = promise; }
    return promise;
  };
  const submit = (value = input) => {
    return startTurn({ value, fileAttachments: attachmentState.resolved() });
  };
  const stop = () => {
    const activeTurn = activeTurnRef.current;
    if (activeTurn?.turnId && activeTurn?.conversationId && cancelTurn) {
      cancelTurn(activeTurn.conversationId, activeTurn.turnId).catch(() => {});
    }
    abortRef.current?.abort();
  };
  const stopAndSend = async () => {
    const content = input.trim();
    const files = attachmentState.resolved();
    if (!content && !files.length) { stop(); return false; }
    const activeTurn = activeTurnRef.current;
    if (!activeTurn) { return submit(content); }
    const parentId = activeTurn.assistantId;
    setInput('');
    if (activeTurn.turnId && activeTurn.conversationId && cancelTurn) {
      await cancelTurn(activeTurn.conversationId, activeTurn.turnId).catch(() => {});
    }
    activeTurn.controller.abort();
    await activeTurn.promise?.catch(() => {});
    return startTurn({ value: content, parentId, includeUser: true, restoreInput: true, fileAttachments: files });
  };
  const editMessage = (item, value, fileAttachments = []) => {
    return startTurn({
      value,
      parentId: item.parentId || null,
      includeUser: true,
      fileAttachments,
      consumeComposerAttachments: false,
    });
  };
  const regenerate = (assistant) => {
    const parent = nodesRef.current.find((node) => { return node.id === assistant.parentId; });
    if (parent?.role === 'user') {
      return startTurn({
        value: parent.content,
        parentId: parent.id,
        includeUser: false,
        restoreInput: false,
        fileAttachments: parent.attachments || [],
        consumeComposerAttachments: false,
      });
    }
    return undefined;
  };
  const navigateBranch = (rootId) => {
    const nextLeaf = latestLeafUnder(nodesRef.current, rootId);
    setActiveLeafId(nextLeaf);
    saveConversation(nodesRef.current, nextLeaf);
  };
  const setFeedback = (id, feedback) => {
    const nextNodes = nodesRef.current.map((node) => { return node.id === id ? { ...node, feedback } : node; });
    nodesRef.current = nextNodes;
    setNodes(nextNodes);
    saveConversation(nextNodes, activeLeafId);
  };

  const updateMode = (next) => {
    setMode(next);
    try { window.localStorage.setItem(modeKey, next); } catch { /* optional */ }
  };

  const fullName = profile?.name?.trim() || profile?.email?.split('@')[0] || '';
  const firstName = fullName.split(/\s+/)[0];
  const hour = new Date().getHours();
  const dayPart = hour < 4 ? 'Working late' : hour < 12 ? 'Good morning' : hour < 18 ? 'Good afternoon' : 'Good evening';
  const greeting = firstName ? `${dayPart}, ${firstName}.` : `${dayPart}.`;
  const inConversation = messages.length > 0;
  const recentConversation = conversations[0] || null;
  const recentRunning = Boolean(recentConversation?.running);
  const chromeHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFF9EE]';
  const historyHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[rgba(111,78,55,0.08)]';

  return (
    <section className={`relative flex ${layoutClassName} min-h-0 flex-col overflow-hidden`} style={{ backgroundColor: t.page }}>
      <header className={`relative z-30 flex min-h-[70px] shrink-0 items-center justify-between gap-3 px-4 sm:px-6 lg:px-8 ${t.name === 'console' ? 'pt-5 pb-3' : 'py-3'}`}>
        <button type="button" onClick={startNewChat} className={`flex h-9 items-center gap-2 rounded-lg border px-3 text-sm font-semibold transition-colors ${chromeHover} focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.heading, borderColor: t.border, backgroundColor: t.statBg }}><Icon name="edit" size={16} />New chat</button>
        <div className="flex items-center gap-2">
          <div ref={historyRef} className="relative">
            <button type="button" aria-label="Chat history" aria-expanded={historyOpen} onClick={() => { setHistoryOpen((open) => { return !open; }); }} className={`flex h-9 w-9 items-center justify-center rounded-lg border transition-colors ${chromeHover} focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.muted, borderColor: t.border, backgroundColor: t.statBg }}><Icon name="clock" size={17} /></button>
            <AnimatedPopover
              open={historyOpen}
              className="absolute right-0 top-full z-50 mt-2 w-[min(340px,calc(100vw-32px))] rounded-xl border p-2 shadow-xl"
              style={{ backgroundColor: t.surface, borderColor: t.border }}
            >
              <div className="px-3 py-2 text-sm font-semibold" style={{ color: t.heading }}>Previous chats</div>
              <HistoryList conversations={conversations} activeId={activeConversationId} onSelect={selectConversation} />
            </AnimatedPopover>
          </div>
        </div>
      </header>

      {inConversation ? (
        <div className="flex min-h-0 flex-1 flex-col">
          <div ref={conversationScrollRef} onScroll={(event) => { const target = event.currentTarget; followLatestRef.current = target.scrollHeight - target.scrollTop - target.clientHeight < 96; }} className="cc-hide-scrollbar min-h-0 flex-1 overflow-y-auto px-4 py-8 sm:px-8">
            <div className="mx-auto flex w-full max-w-[720px] flex-col gap-7">
              {messages.map((item) => {
                return <MessageRow key={item.id} item={item} nodes={nodes} busy={loading} onEdit={editMessage} onFeedback={setFeedback} onRegenerate={regenerate} onNavigateBranch={navigateBranch} onPreviewAttachment={setPreviewAttachment} submitSupportRequest={submitSupportRequest} enableSupportDraft={enableSupportDraft} getAttachmentUrl={getAttachmentUrl} />;
              })}
            </div>
          </div>
          <div className="sticky bottom-0 shrink-0 px-4 pb-5 pt-3 sm:px-8" style={{ background: `linear-gradient(to bottom, transparent, ${t.page} 22%)` }}>
            <div className="mx-auto w-full max-w-[720px]"><Composer value={input} onChange={setInput} onSubmit={() => { submit(); }} onStop={stop} onStopAndSend={stopAndSend} loading={loading} mode={mode} onModeChange={updateMode} compact placeholder={composerPlaceholder} addContextOptions={addContextOptions} attachments={attachmentState} onPreviewAttachment={setPreviewAttachment} enableAttachments={enableAttachments} getAttachmentUrl={getAttachmentUrl} /><p className="mt-2 text-center text-[11px]" style={{ color: t.muted }}>{disclaimer}</p></div>
          </div>
        </div>
      ) : (
        <div className="cc-hide-scrollbar mx-auto flex min-h-0 w-full max-w-[1080px] flex-1 flex-col overflow-y-auto px-4 pb-8 sm:px-6">
          <div className="min-h-[70px] flex-[0.72] shrink-0" />
          <div className="relative z-20 mx-auto flex w-full max-w-[720px] flex-col items-center text-center">
            <div className="mb-5 flex items-center gap-3">
              {renderIdentity?.()}
            </div>
            <h1 className={`${recentConversation ? 'mb-4' : 'mb-6'} text-[28px] leading-tight sm:text-[34px]`} style={{ color: t.heading, fontFamily: t.fonts.display, fontWeight: 500 }}>{greeting}</h1>
            {recentConversation ? (
              <button
                type="button"
                onClick={() => { selectConversation(recentConversation); }}
                aria-label={recentRunning
                  ? `Pick up where you left off, ${recentConversation.title}, a reply is still generating`
                  : `Continue where you left off, ${recentConversation.title}`}
                className={`mb-6 flex max-w-full items-center gap-3 rounded-xl border px-4 py-2.5 text-left transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing} ${t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFF9EE]'}`}
                style={{ borderColor: t.border, backgroundColor: t.statBg, color: t.heading }}
              >
                {recentRunning
                  ? <Spinner size={16} className="shrink-0" />
                  : <Icon name="cornerUpLeft" size={16} />}
                <span className="min-w-0 flex-1">
                  <span className="block text-sm font-semibold">
                    {recentRunning ? 'Pick up where you left off' : 'Continue where you left off'}
                  </span>
                  <span className="block max-w-[320px] truncate text-xs" style={{ color: t.muted }}>
                    {recentConversation.title}
                    {' · '}
                    {recentRunning ? 'Still generating a reply' : formatChatTime(recentConversation.updatedAt)}
                  </span>
                </span>
                <Icon name="arrowRight" size={16} />
              </button>
            ) : null}
            <Composer value={input} onChange={setInput} onSubmit={() => { submit(); }} onStop={stop} onStopAndSend={stopAndSend} loading={loading} mode={mode} onModeChange={updateMode} placeholder={composerPlaceholder} addContextOptions={addContextOptions} attachments={attachmentState} onPreviewAttachment={setPreviewAttachment} enableAttachments={enableAttachments} getAttachmentUrl={getAttachmentUrl} />
            <div ref={quickRef} className="mt-4 flex w-full flex-wrap justify-center gap-2">
              {/* Theme tokens only — hardcoded kraft (#F7EBD9) + cream ink made
                  console chips/titles unreadable (light-on-light). */}
              {quickActions.map((action) => {
                const open = quickOpen === action.key;
                const chipHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFF9EE]';
                const menuItemHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFFBF6]';
                return (
                  <div key={action.key} className="relative">
                    <button
                      type="button"
                      aria-expanded={open}
                      onClick={() => { setQuickOpen(open ? null : action.key); }}
                      className={`flex min-h-[42px] items-center gap-2 rounded-xl border px-4 text-sm font-semibold transition-colors focus:outline-none focus-visible:ring-2 ${t.focusRing} ${open ? '' : chipHover}`}
                      style={{
                        color: open ? t.primaryText : t.heading,
                        borderColor: open ? t.primaryBorder : t.hairline,
                        backgroundColor: open ? t.primaryBg : t.statBg,
                      }}
                    >
                      <Icon name={action.icon} size={14} />
                      {action.label}
                    </button>
                    {open ? (
                      <div
                        className="absolute left-1/2 top-full z-50 mt-2 w-[min(310px,calc(100vw-32px))] -translate-x-1/2 rounded-xl border p-1.5 text-left shadow-xl"
                        style={{ backgroundColor: t.surface, borderColor: t.border }}
                      >
                        {action.items.map(([label, prompt]) => {
                          return (
                            <button
                              key={label}
                              type="button"
                              onClick={() => { submit(prompt); }}
                              className={`w-full rounded-lg px-3 py-2.5 text-left text-sm ${menuItemHover}`}
                              style={{ color: t.body }}
                            >
                              {label}
                            </button>
                          );
                        })}
                      </div>
                    ) : null}
                  </div>
                );
              })}
            </div>
          </div>

          <div className="min-h-[88px] flex-1 pt-8">
            <div className="mx-auto w-full max-w-[720px] text-left">
              <p className="mb-3 text-sm font-semibold" style={{ color: t.heading }}>{emptySectionTitle}</p>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
                {projectCards.map((card) => {
                  const cardHover = t.name === 'console' ? 'hover:bg-[#2A1A14]' : 'hover:bg-[#FFF9EE]';
                  return (
                    <button
                      key={card.title}
                      type="button"
                      onClick={() => { submit(card.prompt); }}
                      className={`min-h-[122px] rounded-xl border p-3.5 text-left transition-colors hover:border-[#E14E2C] focus:outline-none focus-visible:ring-2 ${t.focusRing} ${cardHover}`}
                      style={{ color: t.heading, borderColor: t.hairline, backgroundColor: t.statBg }}
                    >
                      <Icon name={card.icon} size={20} />
                      <span className="mt-3 block text-sm font-semibold" style={{ color: t.heading }}>{card.title}</span>
                      <span className="mt-1.5 block text-sm leading-5" style={{ color: t.muted }}>{card.description}</span>
                    </button>
                  );
                })}
              </div>
              <div className="mt-5 max-w-md">
                <button type="button" aria-expanded={inlineHistoryOpen} onClick={() => { setInlineHistoryOpen((open) => { return !open; }); }} className={`-ml-2 flex items-center gap-2 rounded-lg px-2 py-1 text-sm font-semibold transition-colors ${historyHover} focus:outline-none focus-visible:ring-2 ${t.focusRing}`} style={{ color: t.body }}><Icon name="clock" size={16} />Previous chats <span className={`cc-ai-chevron inline-flex ${inlineHistoryOpen ? 'cc-ai-chevron-open' : ''}`}><Icon name="chevron" size={14} /></span></button>
                <AnimatedCollapse open={inlineHistoryOpen} className="mt-1">
                  <div className="cc-hide-scrollbar max-h-[280px] overflow-y-auto py-1">
                    <HistoryList conversations={conversations} activeId={activeConversationId} onSelect={selectConversation} />
                  </div>
                </AnimatedCollapse>
              </div>
            </div>
          </div>
        </div>
      )}
      <AttachmentPreviewModal attachment={previewAttachment} onClose={() => { setPreviewAttachment(null); }} getAttachmentUrl={getAttachmentUrl} />
    </section>
  );
}
